// ============================================================
// battery.js
// EV Powertrain Simulator — core 모듈
// 배터리 물리 — OCV, EKF/UKF SOC, SOH, 2RC 모델, BMS
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================

// ── OCV LUT — SST: ../../constants/battery_lut.js ───────────────────────
import {
  OCV_LUT_SOC_DEFAULT, OCV_LUT_V_DEFAULT,
  OCV_TEMP_SOC_DEFAULT, OCV_TEMP_DV_N20_DEFAULT,
  OCV_TEMP_DV_P0_DEFAULT, OCV_TEMP_DV_P45_DEFAULT,
} from '../../constants/battery_lut.js';

// ── 물리 상수 — SST: ../../constants/physics_consts.js ──────────────────
// [Fix-C2] R_GAS, F_FARADAY 인라인 선언(4곳) → import 단일화
import { R_GAS, F_FARADAY } from '../../constants/physics_consts.js';

// ── 수학 유틸 — SST: ./math_utils.js ────────────────────────────────────
// [Fix-Issue1-battery] lerpLUT·derateScale 공유 스코프 암묵 의존 → 명시적 import
import { lerpLUT, derateScale } from './math_utils.js';

// ── OCV LUT 바인딩 ──
// [Fix-②] globalThis.OCV_LUT_* 런타임 오버라이드 패턴 제거:
//   문제: 모듈 최상위에서 const로 캡처 → 초기화 순서에 의존, globalThis 오염, 테스트 격리 불가
//   수정: import된 _DEFAULT 상수를 직접 사용 (테스트 시 모듈 mock 가능)
//   LUT 교체 경로: batteryOCV_LUT(soc, lut_soc, lut_v) 시그니처로 per-call 교체 가능.
//   params.ocv_lut_soc / params.ocv_lut_v 를 통해 함수별로 전달 (Fix-②-b 완료).
//   멀티 케미스트리 비교 시뮬레이션: createOCVFunctions(lut_soc, lut_v) 팩토리 사용.
const OCV_LUT_SOC     = OCV_LUT_SOC_DEFAULT;
const OCV_LUT_V       = OCV_LUT_V_DEFAULT;
const OCV_TEMP_SOC    = OCV_TEMP_SOC_DEFAULT;
const OCV_TEMP_DV_N20 = OCV_TEMP_DV_N20_DEFAULT;
const OCV_TEMP_DV_P0  = OCV_TEMP_DV_P0_DEFAULT;
const OCV_TEMP_DV_P45 = OCV_TEMP_DV_P45_DEFAULT;

// ── SOH_PROFILES — SST: ../../constants/sohProfiles.js ──────────────────
// [Fix-②] globalThis.SOH_PROFILES 오버라이드 제거 — OCV_LUT_* 와 동일 정책 적용
import { SOH_PROFILES as _SOH_PROFILES_DEFAULT } from '../../constants/sohProfiles.js';
const SOH_PROFILES = _SOH_PROFILES_DEFAULT;

// ── R_int_T ──
/**
 * 온도·SOC 의존 배터리 내부 저항 산출
 *
 * 내부 저항에 세 가지 보정 인수를 적용한다:
 *   1. SOC 의존: soc_f = 1 + 0.5·exp(−10·SOC) + 0.3·exp(−10·(1−SOC))
 *      → 저SOC/고SOC 양 끝단에서 저항 증가 (리튬 고갈/포화 임피던스 상승)
 *   2. 저온 Arrhenius 보정: cold_f = 1 + k_R_batt·(exp((T_ref−T)/τ) − 1)
 *      → τ=25°C, T_ref=25°C 기준, 저온에서 지수적 저항 증가
 *   3. 고온 보정: hot_f = 1 + 0.005·(T−45) (T > 45°C에서 선형 증가)
 *
 * @param {number} soc    - 충전 상태 [0, 1]
 * @param {number} T_batt - 배터리 온도 [°C]
 * @param {object} params
 *   params.R_batt         - 기준 내부 저항 [Ω]
 *   params.k_R_batt       - 저온 지수 보정 계수 (기본 0.6)
 *   params.T_ref          - 기준 온도 [°C] (기본 25)
 *   params.K_batt_r       - 저항 스케일 캘리브레이션 계수 (기본 1.0)
 * @returns {number}       - 보정된 내부 저항 [Ω]
 */
function R_int_T(soc, T_batt, params) {
  const soc_f = 1.0 + 0.5 * Math.exp(-10 * soc) + 0.3 * Math.exp(-10 * (1 - soc));
  // [Fix-Ri-Exp] 저온 계수를 선형(linear)에서 지수(Arrhenius 근사)로 교체.
  // 구형 선형 cold_f = 1 + k * ΔT 는 −20°C에서 고작 2.35× → 실측 LiNMC 3~5× 대비 낙관적.
  // 수정: cold_f = 1 + k_R_batt × (exp((T_ref−T_batt)/τ) − 1),  τ = 25°C (고정)
  //   k_R_batt=0.6(기본값) 적용 시:
  //     T=25°C → ×1.00 (기준)
  //     T= 0°C → ×2.03  (LiNMC 실측 1.8~2.0× 부합)
  //     T=−20°C → ×4.03 (LiNMC 실측 3~5× 부합)
  //     T=−30°C → ×5.82 (LiNMC 극저온 부합)
  const T_ref = params.T_ref || 25;
  const tau_R = 25; // [K] Arrhenius 스케일 상수 (고정)
  const cold_f = T_batt >= T_ref
    ? 1.0
    : 1.0 + params.k_R_batt * (Math.exp((T_ref - T_batt) / tau_R) - 1);
  const hot_f = T_batt > 45 ? 1.0 + 0.005 * (T_batt - 45) : 1.0;
  return params.R_batt * soc_f * cold_f * hot_f * (params.K_batt_r || 1.0);
}

// ── P_dis_max_T ──
/**
 * 온도 의존 최대 방전 전력 산출
 *
 * 저온(cold) 및 고온(hot) 디레이팅을 중첩 적용한다:
 *   cold 팩터: 구간별 선형 보간 (T<25°C → 점진 감소, 최소 0.15)
 *   hot  팩터: derateScale(T, T_start, T_end) (25~100°C 선형 디레이팅)
 *   최종: P_dis_ref × min(cold, hot) [W]
 * Arbitration 방전 한계(arb_dischg_limit_kw)와 비교해 최솟값 반환.
 *
 * @param {number} T_batt - 배터리 온도 [°C]
 * @param {object} params
 *   params.P_dis_ref            - 기준 최대 방전 전력 [W]
 *   params.T_batt_derate_start  - 고온 디레이팅 시작 온도 [°C]
 *   params.T_batt_derate_end    - 고온 완전 차단 온도 [°C]
 *   params.arb_dischg_limit_kw  - Arbitration 방전 한계 [kW] (기본 999)
 * @returns {number}             - 디레이팅 후 최대 방전 전력 [W]
 */
function P_dis_max_T(T_batt, params) {
  // [R-19-inline-B] coldDerateDischarge 인라인
  const cold = T_batt >= 25 ? 1.0 :
               T_batt >= 10 ? 0.7 + 0.3*(T_batt-10)/15 :
               T_batt >= 0  ? 0.4 + 0.3*T_batt/10 :
               T_batt >= -10? 0.15 + 0.25*(T_batt+10)/10 : 0.15;
  const hot  = derateScale(T_batt, params.T_batt_derate_start, params.T_batt_derate_end);
  const P_thermal = params.P_dis_ref * Math.min(cold, hot);
  // [A10] Arbitration 방전 한계 클램프
  const P_arb = (params.arb_dischg_limit_kw || 999) * 1000;
  return Math.min(P_thermal, P_arb);
}

// ── P_chg_max_T ──
/**
 * 온도·SOC 의존 최대 충전 전력 산출
 *
 * 충전 전력에 두 가지 디레이팅 인수를 곱한다:
 *   _fT  : 저온 온도 팩터 (T<25°C 구간별 선형, 최소 0.05)
 *   _fSoc: SOC > 90% 구간에서 선형 감소 → CC→CV 전환 모사
 *   최종: P_chg_ref × _fT × _fSoc [W]
 * Arbitration 충전 한계 및 BMS 최대 전류 한계와 비교해 최솟값 반환.
 *
 * @param {number} T_batt - 배터리 온도 [°C]
 * @param {number} soc    - 충전 상태 [0, 1]
 * @param {object} params
 *   params.P_chg_ref        - 기준 최대 충전 전력 [W]
 *   params.arb_chg_limit_kw - Arbitration 충전 한계 [kW] (기본 999)
 *   params.bms_i_charge_max - BMS 최대 충전 전류 [A] (기본 9999)
 *   params.V_dc             - 팩 전압 [V]
 * @returns {number}        - 디레이팅 후 최대 충전 전력 [W]
 */
function P_chg_max_T(T_batt, soc, params) {
  // [R-19-inline-B] coldDerateCharge 인라인
  let _fT = 1.0;
  if (T_batt < 25) _fT = Math.max(0.05, 0.3 + 0.7*Math.max(0,T_batt)/25);
  if (T_batt < 0)  _fT = Math.max(0.05, 0.05 + 0.25*Math.max(0,T_batt+10)/10);
  const _fSoc = soc > 0.9 ? Math.max(0, 1-(soc-0.9)/0.1) : 1.0;
  const P_thermal = params.P_chg_ref * _fT * _fSoc;
  // [A10] Arbitration 충전 한계 클램프
  const P_arb = (params.arb_chg_limit_kw || 999) * 1000;
  // [A10] BMS 최대 충전 전류 기반 한계 (I_max × V_dc)
  const P_bms_i = (params.bms_i_charge_max || 9999) * (params.V_dc || 400);
  return Math.min(P_thermal, P_arb, P_bms_i);
}

// ── initHysteresisState ──
/**
 * OCV 히스테리시스 상태 초기화 (Plett 2004)
 * LiFePO₄, NMC 등 수십 mV 히스테리시스가 존재하는 셀에서 SOC 오차를 줄인다.
 * 모델:  dh/dt = γ · (h_inf(i) − h) · |i/Q_nom|
 *   h     : 히스테리시스 전압 상태 [-h_max, +h_max] [V/cell]
 *   h_inf : 포화 방향값 — 방전(i>0) → +h_max, 충전(i<0) → −h_max
 *   γ     : 수렴 속도 계수 [무차원/Ah]  (기본 5.0)
 *   h_max : 최대 히스테리시스 폭 [V/cell] (LFP ≈ 0.025, NMC ≈ 0.005)
 */
function initHysteresisState(params) {
  return {
    h: 0,                                         // 초기 히스테리시스 전압 [V/cell]
    h_max: params.ocv_hyst_max    || 0.010,       // 최대 히스테리시스 [V/cell]
    gamma: params.ocv_hyst_gamma  || 5.0,         // 수렴 속도 [1/Ah_norm]
  };
}

/**
 * updateHysteresis — 매 시뮬레이션 스텝 호출
 * @param {object} hyst_state  - initHysteresisState 반환값
 * @param {number} I_batt      - 팩 전류 [A] (양수=방전, 음수=충전)
 * @param {number} dt          - 타임스텝 [s]
 * @param {object} params
 * @returns {object}           - 갱신된 hyst_state
 */
function updateHysteresis(hyst_state, I_batt, dt, params) {
  if (!params.ocv_hyst_enable) return hyst_state;
  const Q_nom   = params.Q_nom || 200;            // [Ah]
  const h_max   = hyst_state.h_max;
  const gamma   = hyst_state.gamma;
  const h_inf   = I_batt >= 0 ? +h_max : -h_max; // 방전→+, 충전→−
  const dAh     = Math.abs(I_batt) * dt / 3600;   // 이번 스텝 Ah
  // dh = γ · (h_inf − h) · |dAh / Q_nom|
  const dh = gamma * (h_inf - hyst_state.h) * (dAh / Q_nom);
  hyst_state.h = Math.max(-h_max, Math.min(h_max, hyst_state.h + dh));
  return hyst_state;
}


// ── [Fix-②-b] batteryOCV_LUT: per-call LUT 교체 경로 구현 ──────────────────
// lut_soc / lut_v 인수가 전달되면 해당 LUT를 사용, 미전달 시 모듈 상단 기본 LUT 사용.
// 멀티 셀 케미스트리 비교 시뮬레이션에서 params.ocv_lut_soc / params.ocv_lut_v로 교체.
/**
 * OCV-SOC 룩업 테이블 보간 (셀 전압)
 *
 * SOC를 입력으로 lerpLUT 선형 보간을 통해 OCV(개방 회로 전압) [V/cell]를 반환한다.
 * lut_soc/lut_v 미전달 시 모듈 기본 LUT(NMC 811 기준) 사용.
 * 멀티 케미스트리 비교 시뮬레이션에서는 createOCVFunctions() 팩토리를 사용할 것.
 *
 * @param {number}   soc     - 충전 상태 [0, 1]
 * @param {number[]|null} lut_soc - SOC 브레이크포인트 배열 (null이면 기본 LUT)
 * @param {number[]|null} lut_v   - OCV 전압 배열 [V/cell] (null이면 기본 LUT)
 * @returns {number}         - 셀 OCV [V/cell]
 */
function batteryOCV_LUT(soc, lut_soc, lut_v) {
  return lerpLUT(lut_soc ?? OCV_LUT_SOC, lut_v ?? OCV_LUT_V, soc);
}

// ── [Fix-②-b] _resolveLUT: params에서 LUT 오버라이드 추출 헬퍼 ──────────────
// 모든 batteryOCV_* 호출처에서 사용.
// params.ocv_lut_soc / params.ocv_lut_v가 없으면 null → 기본 LUT fallback.
/**
 * params에서 OCV LUT 오버라이드를 추출하는 헬퍼
 *
 * params.ocv_lut_soc / params.ocv_lut_v가 설정되어 있으면 해당 배열을,
 * 없으면 null을 반환한다. null 전달 시 각 OCV 함수가 기본 LUT로 폴백.
 *
 * @param {object|null} params
 * @returns {{ lut_soc: number[]|null, lut_v: number[]|null }}
 */
function _resolveLUT(params) {
  return {
    lut_soc: params?.ocv_lut_soc ?? null,
    lut_v:   params?.ocv_lut_v   ?? null,
  };
}

// ── ocvTempOffset ──
/**
 * OCV 온도 오프셋 보정 [V/cell]
 *
 * 측정 온도(T_batt)에 따라 OCV LUT에 더해지는 온도 보정 오프셋을 반환한다.
 * 세 가지 기준 온도(−20°C, 0°C, +45°C) LUT를 선형 보간해 임의 온도에 적용:
 *   T ≤ −20°C: DV_N20 LUT
 *   −20 < T ≤ 0°C: DV_N20 ↔ DV_P0 선형 보간
 *   0 < T ≤ 45°C:  DV_P0  ↔ DV_P45 선형 보간
 *   T > 45°C:       DV_P45 LUT
 *
 * @param {number} soc    - 충전 상태 [0, 1] (범위 클램프 내부 처리)
 * @param {number} T_batt - 배터리 온도 [°C]
 * @returns {number}      - OCV 온도 오프셋 [V/cell]
 */
function ocvTempOffset(soc, T_batt) {
  const s = Math.max(0.001, Math.min(0.999, soc));
  if (T_batt <= -20) return lerpLUT(OCV_TEMP_SOC, OCV_TEMP_DV_N20, s);
  if (T_batt <= 0)   {
    const t = (T_batt - (-20)) / 20;
    return lerpLUT(OCV_TEMP_SOC, OCV_TEMP_DV_N20, s) * (1 - t) +
           lerpLUT(OCV_TEMP_SOC, OCV_TEMP_DV_P0, s)  * t;
  }
  if (T_batt <= 45)  {
    const t = T_batt / 45;
    return lerpLUT(OCV_TEMP_SOC, OCV_TEMP_DV_P0, s)  * (1 - t) +
           lerpLUT(OCV_TEMP_SOC, OCV_TEMP_DV_P45, s) * t;
  }
  return lerpLUT(OCV_TEMP_SOC, OCV_TEMP_DV_P45, s);
}

// ── batteryOCV_Nernst ──
/**
 * Nernst 방정식 기반 배터리 OCV 산출 [V/cell]
 *
 * 전기화학적 OCV 모델:
 *   V = V_ref + (R·T_K / (n·F)) · ln(SOC / (1 − SOC)) + α_T·(T − 25)
 *   V_ref  = 3.72 V  (NCM 811 평형 기준)
 *   n_elec = 1       (Li-ion 이동 전자 수)
 *   α_T    = +5e-4 V/°C (양의 온도계수 — 저온에서 OCV 소폭 하락)
 *
 * SOC를 [0.001, 0.999]로 클램프해 ln(0) 발산을 방지한다.
 *
 * @param {number} soc    - 충전 상태 [0, 1]
 * @param {number} T_batt - 배터리 온도 [°C] (미전달 시 25°C 기본)
 * @returns {number}      - 셀 OCV [V/cell]
 */
function batteryOCV_Nernst(soc, T_batt) {
  // [Fix-C2] R_GAS, F_FARADAY → physics_consts.js SST import 사용
  const n_elec = 1;           // Li-ion 이동 전자 수
  const V_ref  = 3.72;        // NCM 811 평형 기준 전압 [V]
  // [BugFix-Nernst-alphaT] Li-ion OCV 온도계수 부호 수정
  // 실제 Li-ion: 저온(T<25°C) → OCV 소폭 하락, 고온 → 소폭 상승
  // alpha_T = +0.0005 V/°C: T<25 → dv_T < 0 (하락), T>25 → dv_T > 0 (상승) ✓
  // 기존 -0.0005: T<25 → dv_T > 0 (상승) ✗
  const alpha_T = +0.0005;    // 온도계수 [V/°C]  (Li-ion 양의 온도계수)

  const T_K    = (T_batt != null ? T_batt : 25) + 273.15;
  const soc_c  = Math.max(0.001, Math.min(0.999, soc));
  const nernst = (R_GAS * T_K) / (n_elec * F_FARADAY) * Math.log(soc_c / (1 - soc_c));
  const dv_T   = alpha_T * ((T_batt != null ? T_batt : 25) - 25);
  return V_ref + nernst + dv_T;
}

// ── batteryOCV_smart ──
// [H2] hyst_h: 히스테리시스 전압 오프셋 [V/cell] — updateHysteresis()로 매 스텝 갱신
//      batteryOCV_smart(soc, params, T_batt, hyst_h)  ← hyst_h 선택적 인수
/**
 * LUT + Nernst 가중 혼합 스마트 OCV 산출 [V_pack]
 *
 * 두 OCV 모델을 가중 혼합해 정확도와 안정성을 동시에 확보한다:
 *   V_blend = w_lut·(V_lut + dV_temp) + w_nernst·V_nernst
 *   V_lut    : LUT 보간 OCV [V/cell]
 *   dV_temp  : ocvTempOffset() 온도 보정 (LUT 항에만 적용)
 *   V_nernst : Nernst 방정식 OCV (온도를 수식 내에서 자체 반영)
 *   w_lut    : LUT 가중치 (기본 0.70)
 *
 * 팩 전압으로 스케일: V_pack = V_blend × (V_dc / (V_nom_cell × n_s))
 * 히스테리시스 항 h_pack [V_pack] 가산 ([H2]).
 *
 * @param {number} soc       - 충전 상태 [0, 1]
 * @param {object} params    - 차량 파라미터 (V_dc, bms_cell_count_s, ocv_lut_weight 등)
 * @param {number} [T_batt]  - 배터리 온도 [°C] (미전달 시 params.T_amb 또는 25°C)
 * @param {number} [hyst_h]  - 히스테리시스 전압 오프셋 [V/cell] (미전달 시 0)
 * @returns {number}         - 팩 OCV [V]
 */
function batteryOCV_smart(soc, params, T_batt, hyst_h) {
  const T   = T_batt !== undefined ? T_batt : (params?.T_amb || 25);
  const w_lut     = params?.ocv_lut_weight  ?? 0.70;   // LUT 가중치
  const w_nernst  = 1 - w_lut;                          // Nernst 가중치
  const V_nom_cell = 3.7;
  const n_s        = params?.bms_cell_count_s || 96;
  const scale      = params ? (params.V_dc / (V_nom_cell * n_s)) : 1;

  const { lut_soc, lut_v } = _resolveLUT(params);
  const V_lut     = batteryOCV_LUT(soc, lut_soc, lut_v);
  const V_nernst  = batteryOCV_Nernst(soc, T);
  // [Fix-OCV-Blend] dV_temp은 LUT(기준온도 측정)에만 적용.
  // Nernst 수식은 이미 T_K를 통해 온도를 반영하므로 이중 보정 방지.
  // 기존: w_lut*V_lut + w_nernst*V_nernst + dV_temp  ← Nernst에도 dV_temp가 비례 가산
  // 수정: w_lut*(V_lut + dV_temp) + w_nernst*V_nernst ← 온도 보정은 LUT 항에만
  const dV_temp   = ocvTempOffset(soc, T);
  const V_cell_blended = w_lut * (V_lut + dV_temp) + w_nernst * V_nernst;
  // [H2] OCV 히스테리시스 반영: hyst_h [V/cell] → 팩 전압으로 스케일
  // hyst_h 미전달 시 0 으로 폴백 → 기존 동작 완전 호환
  const h_pack = (hyst_h || 0) * n_s;
  return V_cell_blended * scale + h_pack;
}

// ── dOCV_dSOC ──
// [Fix-②-b] lut_soc / lut_v 인수로 per-call LUT 오버라이드 지원
/**
 * OCV-SOC LUT의 SOC에 대한 기울기(수치 미분) 산출
 *
 * EKF/UKF 측정 행렬 H의 선형화에 사용한다.
 * 중심 차분법으로 계산: dOCV/dSOC ≈ (OCV(soc+δ) − OCV(soc−δ)) / (2δ), δ = 0.005
 * SOC 범위 초과를 방지하기 위해 [0, 1] 클램프 적용.
 *
 * @param {number}   soc     - 충전 상태 [0, 1]
 * @param {number[]|null} lut_soc - SOC 브레이크포인트 배열 (null이면 기본 LUT)
 * @param {number[]|null} lut_v   - OCV 전압 배열 [V/cell] (null이면 기본 LUT)
 * @returns {number}         - dOCV/dSOC [V/cell / (1)] 기울기
 */
function dOCV_dSOC(soc, lut_soc, lut_v) {
  // Numerical gradient of OCV-SOC LUT
  const delta = 0.005;
  const v1 = batteryOCV_LUT(Math.min(1, soc + delta), lut_soc, lut_v);
  const v0 = batteryOCV_LUT(Math.max(0, soc - delta), lut_soc, lut_v);
  return (v1 - v0) / (2 * delta);
}

// ── initEKF_SOC ──
/**
 * 확장 칼만 필터(EKF) SOC 추정 상태 초기화
 *
 * 상태 벡터 x=SOC (스칼라), 공분산 P=0.01 (SOC ±10% 초기 불확실도)로 초기화한다.
 * Q(프로세스 노이즈), R(측정 노이즈)에 캘리브레이션 계수를 적용한다.
 *
 * @param {number} soc_init - 초기 SOC [0, 1]
 * @param {object} params
 *   params.Q_kf        - 기본 프로세스 노이즈 분산 (기본 1e-5)
 *   params.R_kf        - 기본 측정 노이즈 분산 (기본 1e-3)
 *   params.K_kalman_Q  - Q 스케일 보정 계수 (기본 1.0)
 *   params.K_kalman_R  - R 스케일 보정 계수 (기본 1.0)
 * @returns {{ x: number, P: number, Q: number, R: number }}
 */
function initEKF_SOC(soc_init, params) {
  const Q = (params.Q_kf || 1e-5) * (params.K_kalman_Q || 1.0);
  const R = (params.R_kf || 1e-3) * (params.K_kalman_R || 1.0);
  return { x: soc_init, P: 0.01, Q, R };
}

// ── ekfUpdateSOC ──
/**
 * EKF(확장 칼만 필터)로 SOC를 업데이트한다.
 * [Fix-EKF-2RC] 측정 방정식: V = OCV(SOC) − I·R0 − V_C1 − V_C2.
 * rc_state 미전달 시 단순 Ri 모델로 폴백(하위 호환).
 * [Fix-EKF-Joseph] Joseph form P 업데이트로 수치 안정성 보장.
 *
 * @param {{ x, P, Q, R }} ekf              initEKF_SOC() 상태
 * @param {number} V_terminal               단자 전압 측정값 [V]
 * @param {number} I_batt                   배터리 전류 [A]
 * @param {number} soc_coulomb              쿨롱 카운팅 SOC 예측값 [0,1]
 * @param {object} params                   R0_base, R_batt, V_dc, bms_cell_count_s 등
 * @param {{ V1, V2 }} [rc_state]           2RC 상태 (선택; 없으면 단순 Ri 모델)
 * @returns {{ soc, P, K, innov }}
 * @see Plett (2004) §4.2
 */
function ekfUpdateSOC(ekf, V_terminal, I_batt, soc_coulomb, params, rc_state) {
  // [Fix-Dead-Code] !kalman_enable 가드 제거: kalmanSOCUpdate()가 상위에서 이미 처리.
  //   직접 호출 시 가드가 필요하면 호출부에서 처리할 것.
  // Prediction
  const x_pred = Math.max(0.001, Math.min(0.999, soc_coulomb));
  const P_pred = ekf.P + ekf.Q;
  // [Fix-EKF-2RC] 측정 모델 선택: rc_state 유무로 자동 분기
  const has_rc = rc_state && (rc_state.V1 !== undefined) && (rc_state.V2 !== undefined);
  // R0: 2RC 직렬 저항(R0_base) 또는 단순 Ri(R_batt) — 하위 호환 fallback
  const R0  = has_rc
    ? (params.R0_base || params.R_batt || 0.08)
    : (params.R_batt || 0.08);
  // RC 전압 강하: 2RC 상태의 V1·V2 합산 (없으면 0 — 기존 동작)
  const V_rc = has_rc ? (rc_state.V1 + rc_state.V2) : 0;
  // Scale OCV LUT to pack voltage
  const V_nom_cell = 3.7, n_s = params.bms_cell_count_s || 96;
  const { lut_soc: ekf_lut_soc, lut_v: ekf_lut_v } = _resolveLUT(params);
  const V_oc_pred = batteryOCV_LUT(x_pred, ekf_lut_soc, ekf_lut_v) * params.V_dc / (V_nom_cell * n_s);
  // [Fix-EKF-2RC] 측정 예측: V = OCV − I·R0 − V_C1 − V_C2
  const V_pred = V_oc_pred - I_batt * R0 - V_rc;
  const V_meas = V_terminal;
  const innov = V_meas - V_pred;
  // Linearization: H = dOCV/dSOC (scaled)
  const H = dOCV_dSOC(x_pred, ekf_lut_soc, ekf_lut_v) * params.V_dc / (V_nom_cell * n_s);
  const S = H * P_pred * H + ekf.R;
  const K = P_pred * H / Math.max(S, 1e-10);
  const x_upd = Math.max(0, Math.min(1, x_pred + K * innov));
  // [Fix-EKF-Joseph] Joseph form으로 수치 안정성 개선
  // 표준형 P = (1-KH)·P_pred 는 P < 0 발산 가능
  // Joseph form: P = (1-KH)²·P_pred + K²·R  → 항상 양수 보장
  const IKH  = 1 - K * H;
  const P_upd = IKH * IKH * P_pred + K * K * ekf.R;
  return { soc: x_upd, P: P_upd, K: +K.toFixed(6), innov: +innov.toFixed(4) };
}

// ── initUKF_SOC ──
/**
 * 무향 칼만 필터(UKF) SOC 추정 상태 초기화
 *
 * UKF 시그마 포인트 가중치를 초기화한다 (n=1 스칼라 상태):
 *   λ = α²·(n + κ) − n
 *   Wm0 = λ / (n + λ),  Wc0 = Wm0 + (1 − α² + β)
 *   Wi  = 1 / (2·(n + λ))
 * α=1e-3(분산), β=2(가우시안 최적), κ=0(기본)이 권장값.
 *
 * @param {number} soc_init   - 초기 SOC [0, 1]
 * @param {object} params
 *   params.ukf_alpha   - 시그마 포인트 분산 파라미터 α (기본 1e-3)
 *   params.ukf_beta    - 이차항 가중치 β (기본 2.0, 가우시안 최적)
 *   params.ukf_kappa   - 스케일링 파라미터 κ (기본 0)
 *   params.Q_kf, params.R_kf, params.K_kalman_Q, params.K_kalman_R — EKF와 동일
 * @returns {object}   - UKF 상태 (x, P, Q, R, lambda, Wm0, Wc0, Wi, alpha, n)
 */
function initUKF_SOC(soc_init, params) {
  const Q = (params.Q_kf || 1e-5) * (params.K_kalman_Q || 1.0);
  const R = (params.R_kf || 1e-3) * (params.K_kalman_R || 1.0) * 0.5; // UKF typically needs less R
  const alpha = params.ukf_alpha || 1e-3;
  const beta = params.ukf_beta || 2.0;
  const kappa = params.ukf_kappa || 0.0;
  const n = 1; // state dimension (SOC only)
  const lambda = alpha * alpha * (n + kappa) - n;
  const Wm0 = lambda / (n + lambda);
  const Wc0 = Wm0 + (1 - alpha * alpha + beta);
  const Wi = 1 / (2 * (n + lambda));
  return { x: soc_init, P: 0.01, Q, R, lambda, Wm0, Wc0, Wi, alpha, n };
}

// ── ukfUpdateSOC ──
/**
 * UKF(무향 칼만 필터)로 SOC를 업데이트한다.
 * 3개 시그마 포인트(x, x±√((n+λ)P))에 측정 모델 V=OCV(σ)−I·R0−V_C1−V_C2 적용.
 * rc_state 미전달 시 단순 Ri 모델로 폴백(하위 호환).
 *
 * @param {object} ukf          initUKF_SOC() 상태 { x, P, Q, R, lambda, Wm0, Wc0, Wi, n }
 * @param {number} V_terminal   단자 전압 측정값 [V]
 * @param {number} I_batt       배터리 전류 [A]
 * @param {number} soc_coulomb  쿨롱 카운팅 SOC 예측값 [0,1]
 * @param {object} params       R0_base, R_batt, V_dc, bms_cell_count_s 등
 * @param {{ V1, V2 }} [rc_state]  2RC 상태 (선택)
 * @returns {{ soc, P, K, innov }}
 */
function ukfUpdateSOC(ukf, V_terminal, I_batt, soc_coulomb, params, rc_state) {
  // [Fix-Dead-Code] !kalman_enable 가드 제거: kalmanSOCUpdate()가 상위에서 이미 처리.
  const n = ukf.n, lambda = ukf.lambda;
  // Prediction step
  const x_pred = Math.max(0.001, Math.min(0.999, soc_coulomb));
  const P_pred = ukf.P + ukf.Q;
  // Generate sigma points
  const spread = Math.sqrt((n + lambda) * Math.max(P_pred, 1e-8));
  const sigma = [x_pred, x_pred + spread, x_pred - spread];
  const Wm = [ukf.Wm0, ukf.Wi, ukf.Wi];
  const Wc = [ukf.Wc0, ukf.Wi, ukf.Wi];
  // [Fix-EKF-2RC] rc_state 분기 (EKF와 동일 로직)
  const has_rc = rc_state && (rc_state.V1 !== undefined) && (rc_state.V2 !== undefined);
  const R0  = has_rc
    ? (params.R0_base || params.R_batt || 0.08)
    : (params.R_batt || 0.08);
  const V_rc = has_rc ? (rc_state.V1 + rc_state.V2) : 0;
  const V_nom_cell = 3.7, n_s = params.bms_cell_count_s || 96;
  const { lut_soc: ukf_lut_soc, lut_v: ukf_lut_v } = _resolveLUT(params);
  const y_sigma = sigma.map(s => {
    const s_c = Math.max(0.001, Math.min(0.999, s));
    const V_oc = batteryOCV_LUT(s_c, ukf_lut_soc, ukf_lut_v) * params.V_dc / (V_nom_cell * n_s);
    // [Fix-EKF-2RC] V = OCV(σ) − I·R0 − V_C1 − V_C2
    return V_oc - I_batt * R0 - V_rc;
  });
  const y_pred = Wm.reduce((acc, w, i) => acc + w * y_sigma[i], 0);
  // Compute covariances
  const Pyy = Wc.reduce((acc, w, i) => acc + w * (y_sigma[i] - y_pred) ** 2, 0) + ukf.R;
  const Pxy = Wc.reduce((acc, w, i) => acc + w * (sigma[i] - x_pred) * (y_sigma[i] - y_pred), 0);
  const K = Pxy / Math.max(Pyy, 1e-10);
  const innov = V_terminal - y_pred;
  const x_upd = Math.max(0, Math.min(1, x_pred + K * innov));
  // [Fix-UKF-Joseph] Joseph form: P = (1−K·H_eff)²·P_pred + K²·R
  //   H_eff = Pxy/P_pred (유효 선형화 이득). 표준형 P-K·Pyy·K는 수치 오차로 음수 가능.
  //   Joseph form은 항상 양정 보장. EKF와 동일 정책 적용.
  const H_eff = Pxy / Math.max(P_pred, 1e-10);
  const IKH   = 1 - K * H_eff;
  const P_upd = Math.max(1e-8, IKH * IKH * P_pred + K * K * ukf.R);
  return { soc: x_upd, P: P_upd, K: +K.toFixed(6), innov: +innov.toFixed(4) };
}

// ── kalmanSOCUpdate ──
// [Policy-SOC] SOC 추정 경로 정책 (단일 진입점: kalmanSOCUpdate)
//
//   params.kalman_enable = false  → 쿨롱 카운팅(socUpdate) 직접 반환, KF 없음
//   params.kalman_type   = 'EKF'  → ekfUpdateSOC  (비선형 OCV 모델 기반, 중간 연산비용)
//   params.kalman_type   = 'UKF'  → ukfUpdateSOC  (Unscented, 강한 비선형에 유리, 고연산비용)
//   params.kalman_type   = 'linear'(default) → 선형 KF (저연산, 빠른 드라이브 사이클)
//
//   simulation.js 호출 흐름:
//     1단계: soc_coulomb = socUpdate(soc, P_elec, dt, params)  [쿨롱 예측]
//     2단계: kf_result   = kalmanSOCUpdate(kf_state, V_t, I, soc_coulomb, soc_meas, params)  [KF 보정]
//     최종 SOC: kf_result.soc  (kalman_enable=false면 soc_coulomb 그대로)
//
//   Note: socUpdate는 단순 예측자(predictor)이고 kalmanSOCUpdate가 유일한 corrector.
//         두 함수를 simulation.js 외부에서 독립 호출하면 SOC가 이중 갱신될 수 있음.
// ── kalmanSOCUpdate ──
// [Policy-SOC] SOC 추정 경로 정책 (단일 진입점: kalmanSOCUpdate)
//
//   params.kalman_enable = false  → 쿨롱 카운팅(socUpdate) 직접 반환, KF 없음
//   params.kalman_type   = 'EKF'  → ekfUpdateSOC  (비선형 OCV 모델 기반, 중간 연산비용)
//   params.kalman_type   = 'UKF'  → ukfUpdateSOC  (Unscented, 강한 비선형에 유리, 고연산비용)
//   params.kalman_type   = 'linear'(default) → 선형 KF (저연산, 빠른 드라이브 사이클)
//
//   [Fix-EKF-2RC] rc_state: battery2RCModelWithSEI()의 battery_state { V1, V2 } 객체 (선택적)
//     전달 시 EKF/UKF 측정 방정식이 V = OCV − I·R0 − V_C1 − V_C2 로 전환.
//     미전달 시 기존 단순 Ri 모델 유지 (하위 호환).
//
//   simulation.js 호출 흐름:
//     1단계: soc_coulomb = socUpdate(soc, P_elec, dt, params)  [쿨롱 예측]
//     2단계: kf_result   = kalmanSOCUpdate(kf_state, V_t, I, soc_coulomb, soc_meas, params, battery_state)
//     최종 SOC: kf_result.soc  (kalman_enable=false면 soc_coulomb 그대로)
//
//   Note: socUpdate는 단순 예측자(predictor)이고 kalmanSOCUpdate가 유일한 corrector.
//         두 함수를 simulation.js 외부에서 독립 호출하면 SOC가 이중 갱신될 수 있음.
/**
 * SOC 추정 단일 진입점 — 칼만 필터 종류 분기 및 폴백 처리
 *
 * params.kalman_enable=false이면 soc_measured를 그대로 반환(쿨롱 카운팅만).
 * kalman_type에 따라 EKF/UKF/선형KF 중 하나를 호출한다.
 * rc_state 전달 시 2RC 측정 방정식(V = OCV − I·R0 − V_C1 − V_C2)을 적용한다.
 *
 * @param {object} kf_state      - kalman 상태 (initKalmanByType 반환값)
 * @param {number} V_terminal    - 측정된 팩 단자 전압 [V]
 * @param {number} I_batt        - 팩 전류 [A] (양수=방전)
 * @param {number} soc_coulomb   - 쿨롱 카운팅 예측 SOC [0, 1]
 * @param {number} soc_measured  - 관측/초기화 SOC (kalman_enable=false 폴백용)
 * @param {object} params        - 차량 파라미터
 * @param {object} [rc_state]    - 2RC 배터리 상태 { V1, V2 } (선택적)
 * @returns {{ soc: number, P: number, K: number, [innov]: number }}
 */
function kalmanSOCUpdate(kf_state, V_terminal, I_batt, soc_coulomb, soc_measured, params, rc_state) {
  const ktype = params.kalman_type || 'linear';
  if (!params.kalman_enable) return { soc: soc_measured, P: kf_state.P, K: 0 };
  // [Fix-EKF-2RC] rc_state를 EKF/UKF로 전달
  if (ktype === 'EKF') return ekfUpdateSOC(kf_state, V_terminal, I_batt, soc_coulomb, params, rc_state);
  if (ktype === 'UKF') return ukfUpdateSOC(kf_state, V_terminal, I_batt, soc_coulomb, params, rc_state);
  // [R-19-inline-A] _linearKFUpdate 인라인
  const P_pred = kf_state.P + kf_state.Q;
  const K_lin  = P_pred / (P_pred + kf_state.R);
  const x_upd  = soc_coulomb + K_lin * (soc_measured - soc_coulomb);
  const P_upd  = (1 - K_lin) * P_pred;
  return { soc: Math.min(1, Math.max(0, x_upd)), P: P_upd, K: +K_lin.toFixed(4) };
}

// ── initKalmanByType ──
/**
 * params.kalman_type에 따라 적합한 칼만 상태를 초기화
 *
 * 'EKF' → initEKF_SOC  (1차 선형화 기반)
 * 'UKF' → initUKF_SOC  (시그마 포인트 기반)
 * 그 외  → 선형 KF (Q, R만 있는 단순 스칼라 상태)
 *
 * @param {number} soc_init - 초기 SOC [0, 1]
 * @param {object} params   - 차량 파라미터 (kalman_type, Q_kf, R_kf 등)
 * @returns {object}        - 초기화된 KF 상태 객체
 */
function initKalmanByType(soc_init, params) {
  const ktype = params.kalman_type || 'linear';
  if (ktype === 'EKF') return initEKF_SOC(soc_init, params);
  if (ktype === 'UKF') return initUKF_SOC(soc_init, params);
  // [R-19-inline-A] _linearKFInit 인라인
  const Q_lin = (params.Q_kf || 1e-5) * (params.K_kalman_Q || 1.0);
  const R_lin = (params.R_kf || 1e-3) * (params.K_kalman_R || 1.0);
  return { x: soc_init, P: 0.01, Q: Q_lin, R: R_lin };
}

// ── socUpdate ──
/**
 * 쿨롱 카운팅 기반 SOC 예측 (칼만 필터 입력용)
 *
 * SOC 변화율: dSOC/dt = −P_elec / E_batt [J]
 * SOC = SOC_prev − P_elec·dt / (battery_energy × 3.6×10⁶) 로 계산한다.
 * 결과는 [0, 1] 범위로 클램프한다.
 * 이 함수는 predictor 역할이며, 최종 SOC는 kalmanSOCUpdate()가 보정한다.
 *
 * @param {number} soc      - 직전 SOC [0, 1]
 * @param {number} P_elec   - 배터리 소비 전력 [W] (양수=방전, 음수=충전)
 * @param {number} dt       - 타임스텝 [s]
 * @param {object} params
 *   params.battery_energy  - 팩 총 에너지 [kWh]
 * @returns {number}        - 예측 SOC [0, 1]
 */
function socUpdate(soc, P_elec, dt, params) {
  const E_batt_J = params.battery_energy * 3600000;
  return Math.max(0, Math.min(1, soc - P_elec * dt / E_batt_J));
}

// ── computeBatteryLosses ──
/**
 * 배터리 발열 및 OCV 계산
 *
 * 내부 저항 Ri(SOC, T)를 구한 후 I²·Ri 열손실을 계산한다.
 * OCV는 batteryOCV_smart(LUT+Nernst 혼합)로 산출한다.
 *
 * @param {number} I_batt  - 팩 전류 [A] (양수=방전)
 * @param {number} soc     - 충전 상태 [0, 1]
 * @param {number} T_batt  - 배터리 온도 [°C]
 * @param {object} params  - 차량 파라미터 (R_batt, V_dc 등)
 * @returns {{ P_loss: number, Ri: number, V_oc: number, I_batt: number }}
 *   P_loss : 내부 저항 발열 [W]
 *   Ri     : 보정된 내부 저항 [Ω]
 *   V_oc   : 팩 OCV [V]
 *   I_batt : 입력 전류 (패스스루) [A]
 */
function computeBatteryLosses(I_batt, soc, T_batt, params) {
  const Ri    = R_int_T(soc, T_batt, params);
  const P_loss = I_batt * I_batt * Ri;
  // batteryOCV_smart: LUT+Nernst 가중합 (구 batteryOCV 대체)
  const V_oc  = batteryOCV_smart(soc, params, T_batt);
  return { P_loss, Ri, V_oc, I_batt };
}

// ── getSOHProfile ──
/**
 * 셀 케미스트리별 SOH 열화 프로파일 반환
 *
 * params.soh_profile 키로 SOH_PROFILES 테이블을 조회한다.
 * 키가 없거나 미지정 시 'nmc_622' 기본 프로파일을 반환한다.
 *
 * @param {object} params
 *   params.soh_profile - 케미스트리 키 (예: 'nmc_622', 'lfp', 'nca')
 * @returns {object}    - SOH 열화 파라미터 객체 (B_cycle, Ea_cycle, k_cal 등 포함)
 */
function getSOHProfile(params) {
  return SOH_PROFILES[params.soh_profile || 'nmc_622'] || SOH_PROFILES.nmc_622;
}

// ── init2RCBatteryState — SST: ./_shared_stubs.js ──

// ── [Fix-②-b] createOCVFunctions — 멀티 케미스트리 비교용 OCV 함수 팩토리 ────
/**
 * 지정한 OCV-SOC LUT 쌍에 고정된 OCV 함수 집합을 반환한다.
 * 멀티 셀 케미스트리(NMC, LFP, NCA 등) 비교 시뮬레이션에서 케미스트리별로
 * 독립적인 OCV 함수를 생성할 때 사용한다.
 *
 * @param {number[]} lut_soc  SOC 브레이크포인트 배열 [0..1]
 * @param {number[]} lut_v    OCV 전압 배열 [V/cell]
 * @returns {{ ocvLUT, dOCV_dSOC, ocvSmart }}
 *
 * 사용 예:
 *   import { createOCVFunctions } from './battery.js';
 *   import { LFP_LUT_SOC, LFP_LUT_V } from '../../constants/battery_lut.js';
 *   const lfpOCV = createOCVFunctions(LFP_LUT_SOC, LFP_LUT_V);
 *   const v = lfpOCV.ocvLUT(0.5);
 */
function createOCVFunctions(lut_soc, lut_v) {
  if (!Array.isArray(lut_soc) || !Array.isArray(lut_v) || lut_soc.length !== lut_v.length) {
    throw new Error('[createOCVFunctions] lut_soc / lut_v는 동일 길이의 배열이어야 합니다.');
  }
  return {
    /** @param {number} soc */
    ocvLUT: (soc) => batteryOCV_LUT(soc, lut_soc, lut_v),
    /** @param {number} soc */
    dOCV_dSOC: (soc) => dOCV_dSOC(soc, lut_soc, lut_v),
    /** @param {number} soc @param {object} params @param {number} [T_batt] @param {number} [hyst_h] */
    ocvSmart: (soc, params, T_batt, hyst_h) =>
      batteryOCV_smart(soc, { ...params, ocv_lut_soc: lut_soc, ocv_lut_v: lut_v }, T_batt, hyst_h),
  };
}

export { init2RCBatteryState } from './_shared_stubs.js';

// ── battery2RCModelWithSEI ──
/**
 * 2RC 등가회로 배터리 모델 (SEI 성장 + Li-플레이팅 통합)
 *
 * R0 + (R1‖C1) + (R2‖C2) 회로에 SEI 저항 성장과 Li-플레이팅 리스크를 추가한 모델:
 *   V_terminal = OCV − I·R0 − V1 − V2
 *   dV1/dt = I/C1 − V1/τ1,  τ1 = R1·C1
 *   dV2/dt = I/C2 − V2/τ2,  τ2 = R2·C2
 *   R0, R1, R2: SOC·온도 의존 (Arrhenius 보정)
 *   C1, C2: SOC·온도 의존 (alpha_C 온도계수)
 *
 * soh_state 전달 시 SEI 두께 성장 및 Li-플레이팅 전하량을 누적.
 * Butler-Volmer 음극 과전위 η_n < 0 시 플레이팅 발생으로 판정한다.
 *
 * @param {object} battery_state  - 배터리 상태 ({ SOC, V1, V2, T_cell } 포함, 내부 수정됨)
 * @param {number} I_demand       - 요구 전류 [A] (양수=방전, 음수=충전)
 * @param {number} dt             - 타임스텝 [s]
 * @param {object} params         - 차량 파라미터 (R0_base, R1_base, R2_base, C1_base, C2_base 등)
 * @param {object|null} soh_state - SOH 상태 (SEI 추적용, null이면 SEI 비활성)
 * @returns {{ V_terminal, V_oc, P_loss, R0, R1, R2, tau1, tau2, model,
 *             SEI_loss, SEI_thickness, li_plate_risk, eta_n, delta_pl_Ah }}
 */
function battery2RCModelWithSEI(battery_state, I_demand, dt, params, soh_state) {
  // ── 2RC 코어 (구 battery2RCModel 로직 통합) ───────────────────────
  const SOC   = battery_state.SOC;
  const V1    = battery_state.V1 || 0;
  const V2    = battery_state.V2 || 0;
  const T_cell = battery_state.T_cell !== undefined ? battery_state.T_cell : 25;

  // OCV: batteryOCV_smart (LUT + Nernst 가중합, 온도 보정 포함)
  const V_oc   = batteryOCV_smart(SOC, params, T_cell);

  const T_K     = T_cell + 273.15;
  const T_ref_K = (params.T_ref || 25) + 273.15;
  const Ea      = 3000;   // [J/mol] 활성화 에너지 (내부저항 온도 의존)
  // [Fix-C2] R_GAS → physics_consts.js SST import 사용
  const f_temp  = Math.exp(Ea / R_GAS * (1 / T_K - 1 / T_ref_K));

  // SOC-dependent 저항 부스팅 (양단 저SOC/고SOC 영역)
  const k_low  = params.k_R0_soc_low  || 0.5;
  const k_high = params.k_R0_soc_high || 0.3;
  const f_soc  = 1 + k_low * Math.exp(-10 * SOC) + k_high * Math.exp(-10 * (1 - SOC));

  const R0 = (params.R0_base || 0.020) * f_soc * f_temp;
  const R1 = (params.R1_base || 0.015) * (1 + 0.3 * Math.exp(-5 * SOC)) * f_temp;
  const R2 = (params.R2_base || 0.010) * (1 + 0.5 * Math.exp(-3 * SOC)) * f_temp;
  // [M2-Fix] 2RC 캐패시턴스 온도 의존성 추가
  //   C1/C2는 기존에 SOC 의존만 적용 → 저온 임피던스 과소추정
  //   α_C ≈ 0.005/°C (LiNMC 실험값): 저온 → Cdl 감소 → 시상수 변화 → 임피던스 정확화
  //   f_C_temp = 1 + α_C·(T_cell − 25)
  //     T= 25°C → ×1.00 (기준)
  //     T=  0°C → ×0.875 (−12.5%)
  //     T=−20°C → ×0.775 (−22.5%)
  const alpha_C  = params.alpha_C || 0.005;                   // [/°C] 캐패시턴스 온도 계수
  const f_C_temp = Math.max(0.3, 1 + alpha_C * (T_cell - 25)); // 최소 0.3배 클램프 (극저온 수렴)
  const C1 = (params.C1_base || 500)   * (1 - 0.10 * (1 - SOC)) * f_C_temp;
  const C2 = (params.C2_base || 5000)  * (1 - 0.05 * (1 - SOC)) * f_C_temp;
  const tau1 = R1 * C1, tau2 = R2 * C2;

  const dV1 = (I_demand / C1 - V1 / tau1) * dt;
  const dV2 = (I_demand / C2 - V2 / tau2) * dt;
  battery_state.V1 = V1 + dV1;
  battery_state.V2 = V2 + dV2;

  const V_terminal   = V_oc - I_demand * R0 - battery_state.V1 - battery_state.V2;
  const P_loss_R0    = I_demand * I_demand * R0;
  const P_loss_R1    = battery_state.V1 * battery_state.V1 / R1;
  const P_loss_R2    = battery_state.V2 * battery_state.V2 / R2;
  const P_loss_total = P_loss_R0 + P_loss_R1 + P_loss_R2;
  const rc = { V_terminal, V_oc, P_loss: P_loss_total, R0, R1, R2, tau1, tau2, model: '2RC' };

  // ── SEI 저항 증가 + Li-plating 위험도 ────────────────────────────
  if (!soh_state) return { ...rc, SEI_loss: 0, li_plate_risk: false };

  // dSEI/dt = k_sei · exp(-Ea_sei/RT) · |I|  (리튬 플레이팅 모델)
  const k_sei_A  = params.k_sei_A  || 2.5e-7;
  const k_sei_Ea = params.k_sei_Ea || 30000;
  const k_sei    = k_sei_A * Math.exp(-k_sei_Ea / (R_GAS * T_K));
  const dSEI     = k_sei * Math.abs(I_demand) * dt;
  soh_state.SEI_thickness = (soh_state.SEI_thickness || 0) + dSEI;
  const SEI_loss      = soh_state.SEI_thickness * (params.R_sei_coeff || 1500);
  // [H1] Li-plating 정량 모델 — Butler-Volmer 음극 과전위 η_n → Δ_pl[Ah] 축적
  // 기존: li_plate_risk = boolean (I<0 && T<10°C) — 수치 없음
  // 개선: 음극 과전위 η_n = V_cell/n_s − OCV_cell − I_cell·(R0+R1+R2)
  //        η_n < 0  → 리튬 석출 가능 영역
  //        Δ_pl = i0_pl · (exp(−α_a·F·η_n/RT) − 1) · A_anode · dt  [A → Ah 환산]
  //   i0_pl ≈ 1e-4 A/m²  (표준 플레이팅 교환전류밀도, Plett 2015 §5)
  //   A_anode ≈ Q_nom / (C_rate_1C · 3600) → 간략화: 단위전류당 손실로 무차원 처리
  const n_s_cells = params.bms_cell_count_s || 96;
  const n_p_cells = params.bms_cell_count_p || 4;
  const V_cell_pl = rc.V_terminal / n_s_cells;
  const I_cell_pl = I_demand / n_p_cells;
  const OCV_cell_pl = rc.V_oc / n_s_cells;
  // 음극 과전위: 터미널 전압에서 OCV와 저항 강하를 제거
  const eta_n = V_cell_pl - OCV_cell_pl + I_cell_pl * (R0 + (battery_state.V1 / (I_demand || 1e-9) || 0));
  // Butler-Volmer 석출 전류밀도 (충전 중 η_n < 0 일 때만 의미)
  // [Fix-C2] F_FARADAY, R_GAS → physics_consts.js SST import 사용
  const alpha_a  = 0.5;
  const i0_pl    = params.i0_li_plating || 1e-4;   // [A/m²] 교환전류밀도
  const A_anode  = params.A_anode_m2    || 0.1;    // [m²] 음극 유효면적 근사
  let delta_pl_Ah = 0;
  let li_plate_risk = false;
  let eta_n_val = +eta_n.toFixed(5);
  if (I_demand < 0 && eta_n < 0) {           // 충전 중 & 과전위 음수 → 석출 조건
    const i_pl = i0_pl * (Math.exp(-alpha_a * F_FARADAY * eta_n / (R_GAS * T_K)) - 1);
    delta_pl_Ah = Math.max(0, i_pl * A_anode * dt / 3600); // [Ah]
    soh_state.li_plating_Ah = (soh_state.li_plating_Ah || 0) + delta_pl_Ah;
    li_plate_risk = delta_pl_Ah > (params.li_plating_Ah_threshold || 1e-6);
  }
  return {
    ...rc, SEI_loss, SEI_thickness: soh_state.SEI_thickness,
    li_plate_risk,
    eta_n: eta_n_val,
    delta_pl_Ah: +delta_pl_Ah.toExponential(3),
    li_plating_Ah_total: +(soh_state.li_plating_Ah || 0).toFixed(6),
  };
}

// ── initSOHState ──
/**
 * SOH(State of Health) 열화 추적 상태 초기화
 *
 * 사이클 열화(Ah 카운팅), 캘린더 열화(√t 모델), Arrhenius 기반 열화 상태를 초기화한다.
 * updateSOH()에서 매 스텝 누산된다.
 *
 * @param {object} params - 차량 파라미터 (미사용, 확장 대비)
 * @returns {{ SOH: number, SOH_arrhenius: number, cycle_count: number, ... }}
 */
function initSOHState(params) {
  return {
    SOH: 1.0,
    SOH_arrhenius: 1.0,
    cycle_count: 0,
    Q_loss_cycle: 0,
    Q_loss_calendar: 0,
    Q_loss_arrhenius_cal: 0,
    Q_loss_arrhenius_cyc: 0,
    days_elapsed: 0,
    avg_SOC: 0.5,
    avg_T: 25,
    avg_DOD: 0,
    avg_C_rate: 0,
    t_elapsed_s: 0,
    Ah_total: 0,
    li_plating_events: 0
  };
}

// ── updateSOH ──
/**
 * SOH 열화 상태 업데이트 (매 시뮬레이션 스텝 호출)
 *
 * 두 가지 열화 경로를 병렬 계산하고 params.soh_model로 선택한다:
 *   1. 기본(Ah 카운팅) 모델:
 *      - 사이클 열화: B_cycle·exp(-Ea/RT)·DOD^z_DOD (Arrhenius × DOD 멱함수)
 *      - 캘린더 열화: k_cal·exp(-Ea/RT)·SOC^α·Δ√(t/365) (해석적 증분)
 *   2. Arrhenius 모델 (기본 선택):
 *      - 캘린더: A_cal·exp(-Ea/R·(1/T−1/T_ref))·t^n
 *      - 사이클: B_cycle·exp(...)·Ah_norm^z_DOD
 *
 * @param {object} soh_state     - initSOHState 반환 상태 (내부 수정됨)
 * @param {object} battery_state - 배터리 상태 ({ SOC, T_cell })
 * @param {number} I_batt        - 팩 전류 [A]
 * @param {number} dt            - 타임스텝 [s]
 * @param {object} params        - 차량 파라미터 (soh_enable, soh_model, B_cycle, k_cal 등)
 * @returns {object}             - 갱신된 soh_state
 */
function updateSOH(soh_state, battery_state, I_batt, dt, params) {
  if (!params.soh_enable) {
    return soh_state;
  }
  
  const dt_hours = dt / 3600;
  const dt_days = dt / 86400;
  
  // 누적 통계
  soh_state.avg_SOC = 0.99 * soh_state.avg_SOC + 0.01 * battery_state.SOC;
  soh_state.avg_T = 0.99 * soh_state.avg_T + 0.01 * battery_state.T_cell;
  
  // C-rate 계산
  const Q_nom = params.Q_nom || 200;
  const C_rate = Math.abs(I_batt) / Q_nom;
  soh_state.avg_C_rate = 0.99 * soh_state.avg_C_rate + 0.01 * C_rate;
  
  // 사이클 카운팅 (간단한 Ah counting)
  soh_state.cycle_ah = (soh_state.cycle_ah || 0) + Math.abs(I_batt) * dt_hours;
  if (soh_state.cycle_ah >= Q_nom * 2) {
    soh_state.cycle_count += 1;
    soh_state.cycle_ah = 0;
  }
  
  // 사이클 열화
  const B_cycle = params.B_cycle || 7.543e-3;
  const Ea_cycle = params.Ea_cycle || 31700;
  const z_DOD = params.z_DOD || 0.55;
  // [Fix-C2] R_GAS → physics_consts.js SST import 사용
  const T_K = soh_state.avg_T + 273.15;
  
  // [Fix-DOD-Dynamic] 하드코딩 0.8 제거 → avg_DOD EMA 추적
  // 순간 DOD 근사: SOC 중간점(0.5)에서의 거리 × 2 (0~1 정규화)
  soh_state.avg_DOD = 0.99 * (soh_state.avg_DOD || 0.8) + 0.01 * Math.abs(battery_state.SOC - 0.5) * 2;
  const DOD_effective = soh_state.avg_DOD;
  const dQ_cycle_rate = B_cycle * Math.exp(-Ea_cycle / (R_GAS * T_K)) * Math.pow(DOD_effective, z_DOD);
  soh_state.Q_loss_cycle += dQ_cycle_rate * (soh_state.cycle_count - (soh_state.prev_cycle_count || 0));
  soh_state.prev_cycle_count = soh_state.cycle_count;
  
  // 캘린더 열화
  // [Fix-Calendar-Accumulation] 기존 코드는 매 스텝마다 전체 기간을 재계산해
  // 덮어씌우는 방식(= 할당)이어서 dt가 불균일하면 값이 흔들렸음.
  // → 점진 누산(+=)으로 수정: 현재 스텝의 증분만 더한다.
  const prev_days = soh_state.days_elapsed;  // 누산 전 일수
  soh_state.days_elapsed += dt_days;
  const k_cal = params.k_cal || 4.14e-10;
  const Ea_cal = params.Ea_cal || 24500;
  const alpha_SOC = params.alpha_SOC || 0.75;

  // [Fix-Issue4] 캘린더 열화 수학 수정 — sqrt(t) 이중적분 제거
  //
  // 물리 모델: Q_loss_cal(t) = k·exp(-Ea/RT)·SOC^α·√(t/365)
  // → 미분(율): dQ/dt = k·(...)·0.5/√(t/365)·(1/365)  — 이것이 진짜 rate
  //
  // 기존 코드 문제:
  //   dQ_cal_rate = k·(...)·√(t)          ← √(t) 자체를 rate로 사용 (오류)
  //   Q_loss += dQ_cal_rate · dt          ← ∫ √(t) dt = t^1.5/1.5 로 수렴 (이중 적분)
  //
  // 수정: 증분(Δ) 방식으로 완전히 교체
  //   dQ_cal = k·(...)·[√(t_new/365) − √(t_prev/365)]
  //   → 수치 적분 없이 해석적 증분으로 ΔQ를 계산 → 스텝 크기 무관하게 정확
  //   → 첫 스텝(prev_days=0)에서 √0=0이므로 안전
  const soc_factor = Math.pow(Math.max(1e-3, soh_state.avg_SOC), alpha_SOC);
  const k_cal_base = k_cal * Math.exp(-Ea_cal / (R_GAS * T_K)) * soc_factor;
  const sqrt_new   = Math.sqrt(soh_state.days_elapsed / 365);
  const sqrt_prev  = Math.sqrt(prev_days / 365);
  const dQ_cal     = k_cal_base * (sqrt_new - sqrt_prev);
  soh_state.Q_loss_calendar += Math.max(0, dQ_cal);
  
  // 총 SOH
  const total_loss = soh_state.Q_loss_cycle + soh_state.Q_loss_calendar;
  soh_state.SOH = Math.max(0.5, 1.0 - total_loss);
  // ── Arrhenius SOH v3 통합 (구 arrheniusSOH_v3 인라인) ───────────────────
  // Calendar aging: Q_loss = A·exp(-Ea/RT)·t^n  (Arrhenius)
  const A_cal  = params.A_arrhenius_cal || 5.0e-4;
  const n_exp  = params.n_arrhenius     || 0.5;
  const t_s    = (soh_state.t_elapsed_s || 0) + dt;
  soh_state.t_elapsed_s = t_s;
  // [KPI-Fix-SOH-Arrhenius] A_cal, B_cycle은 T_ref=25°C에서 경험적으로 산정된
  // rate constant. exp(-Ea/RT) 절대값 적용 시 25°C에서 ~5e-5~3e-6이 이중 곱해져
  // 열화량이 ~10⁵배 과소평가됨. → 상대 Arrhenius 보정(1/T − 1/T_ref) 사용.
  const T_ref_K_arr = 298.15; // 25°C 기준
  const k_cal_arr = A_cal * Math.exp(-Ea_cal / R_GAS * (1 / T_K - 1 / T_ref_K_arr));
  const Q_loss_cal_arr = k_cal_arr * Math.pow(t_s / 3600, n_exp);

  // Cycle aging (Ah 기반)
  const dAh = Math.abs(I_batt) * (dt_hours);
  soh_state.Ah_total = (soh_state.Ah_total || 0) + dAh;
  // [KPI-Fix-CycFormula] 기존: k * DOD^z * Ah_norm (DOD=1 고착 → 선형, z_DOD 무효)
  // 수정: k * (Ah_norm)^z_DOD (표준 멱함수 모델, 단조감소 속도로 현실적)
  const Ah_norm = soh_state.Ah_total / Q_nom;
  const k_cyc_arr = B_cycle * Math.exp(-Ea_cycle / R_GAS * (1 / T_K - 1 / T_ref_K_arr));
  const Q_loss_cyc_arr = k_cyc_arr * Math.pow(Math.max(Ah_norm, 1e-6), z_DOD);

  // Arrhenius 결합 SOH
  soh_state.SOH_arrhenius        = Math.max(0.5, 1.0 - (Q_loss_cal_arr + Q_loss_cyc_arr));
  soh_state.Q_loss_arrhenius_cal = Q_loss_cal_arr;
  soh_state.Q_loss_arrhenius_cyc = Q_loss_cyc_arr;
  soh_state.k_cal                = k_cal_arr;
  soh_state.k_cyc                = k_cyc_arr;

  // [Fix-④] SOH 모델 선택: 기본(Ah-카운팅) / Arrhenius 중 명시적 선택
  //   params.soh_model = 'arrhenius' → SOH_arrhenius 사용 (온도·DOD 의존 고정밀)
  //   params.soh_model = 'basic'     → 기본 Ah 카운팅 SOH 사용 (빠른 추정)
  //   미설정(undefined) + li_plating_enable → 두 모델 중 보수적 값 (기존 동작 유지)
  //   미설정(undefined) + !li_plating_enable → Arrhenius를 기본으로 사용 (더 물리적)
  const _soh_model = params.soh_model || 'arrhenius';  // 기본값을 Arrhenius로 격상
  if (_soh_model === 'arrhenius') {
    soh_state.SOH = soh_state.SOH_arrhenius;
  } else if (_soh_model === 'basic') {
    // 기본 SOH 그대로 유지 (위에서 이미 계산됨)
  } else if (params.li_plating_enable) {
    // 레거시: li_plating_enable 시 보수적 최솟값
    soh_state.SOH = Math.min(soh_state.SOH, soh_state.SOH_arrhenius);
  } else {
    soh_state.SOH = soh_state.SOH_arrhenius;
  }
  return soh_state;
}

// ── applySOHDegradation ──
/**
 * SOH에 따른 배터리 파라미터 열화 적용
 *
 * SOH 열화를 파라미터로 반영한 새 params 객체를 반환한다:
 *   - 유효 에너지: battery_energy × SOH
 *   - 유효 정격 용량: Q_nom × SOH
 *   - 저항 증가: R0/R1/R2 × (1 + 0.5·(1−SOH))  → EOL(SOH=0.5)에서 +25% 증가
 *
 * @param {object} params     - 원본 차량 파라미터 (변경 없음)
 * @param {object} soh_state  - SOH 상태 (updateSOH 반환)
 * @returns {object}          - 열화 파라미터가 반영된 새 params 객체
 */
function applySOHDegradation(params, soh_state) {
  if (!params.soh_enable || !soh_state) {
    return params;
  }
  
  // 용량 감소
  const degraded_params = { ...params };
  degraded_params.battery_energy_effective = params.battery_energy * soh_state.SOH;
  degraded_params.Q_nom_effective = params.Q_nom * soh_state.SOH;
  
  // 저항 증가
  const R_growth = 1 + (1 - soh_state.SOH) * 0.5; // 50% 증가 at EOL
  degraded_params.R0_base = params.R0_base * R_growth;
  degraded_params.R1_base = params.R1_base * R_growth;
  degraded_params.R2_base = params.R2_base * R_growth;
  
  return degraded_params;
}

// ── initSEIState ──
/**
 * SEI(Solid Electrolyte Interphase) 성장 추적 상태 초기화
 *
 * SEI 두께(delta_SEI), 저항(R_sei), 용량 손실(Q_loss_sei),
 * Li-플레이팅 비가역 손실(Q_loss_plating)을 0으로 초기화한다.
 *
 * @param {object} params
 *   params.R_sei_init - SEI 초기 저항 [Ω] (기본 0.001)
 * @returns {{ delta_SEI, R_sei, Q_loss_sei, Q_loss_plating, li_plating_count, t_elapsed }}
 */
function initSEIState(params) {
  return {
    delta_SEI: 0,          // SEI 두께 [nm 단위 무차원]
    R_sei: params.R_sei_init || 0.001,
    Q_loss_sei: 0,         // SEI 캘린더 용량 손실 [Ah]
    Q_loss_plating: 0,     // Li-플레이팅 비가역 손실 [Ah] (SEI와 분리)
    li_plating_count: 0,
    t_elapsed: 0,
  };
}

// ── updateSEI ──
/**
 * SEI 두께 성장 및 Li-플레이팅 감지 업데이트 (매 스텝 호출)
 *
 * SEI 성장 모델 (확산 제한):
 *   d(δ)/dt = k_A·exp(-Ea/R·(1/T−1/T_ref)) / (2·δ + ε)
 *   δ가 두꺼워질수록 성장 속도가 감소하는 확산 제한 특성.
 *   Ea=30000 J/mol (기본), T_ref=25°C 상대 Arrhenius 사용.
 *
 * Li-플레이팅 감지:
 *   충전 중 음극 전위 phi_anode = V_cell − |I_cell|·R_sei < threshold
 *   해당 조건에서 Q_loss_plating 증산 및 plating count 증가.
 *
 * @param {object} sei_state      - SEI 상태 (initSEIState 반환, 내부 수정됨)
 * @param {object} battery_state  - 배터리 상태 ({ T_cell, V_terminal })
 * @param {number} I_batt         - 팩 전류 [A] (음수=충전)
 * @param {number} dt             - 타임스텝 [s]
 * @param {object} params         - 차량 파라미터 (sei_enable, k_SEI_A, k_SEI_Ea 등)
 * @returns {object}              - 갱신된 sei_state
 */
function updateSEI(sei_state, battery_state, I_batt, dt, params) {
  if (!params.sei_enable) return sei_state;
  // [Fix-C2] R_GAS → physics_consts.js SST import 사용
  const T_K   = Math.max(250, (battery_state.T_cell || 25) + 273.15);
  const k_A   = params.k_SEI_A  || 2.5e-7;
  const Ea    = params.k_SEI_Ea || 30000;

  // SEI 두께 성장: d(δ)/dt = k_A·exp(-Ea/R·(1/T−1/T_ref)) / (2·δ+ε)
  // [KPI-Fix-SEI-Arrhenius] k_A는 T_ref=25°C 경험적 rate constant.
  // 절대 Arrhenius(k_A*exp(-Ea/RT))를 쓰면 25°C에서 exp(-Ea/RT)≈5.5e-6이
  // 이중 적용되어 k_eff가 ~10⁶배 과소평가됨. → 상대 보정 사용.
  const T_ref_K_sei = 298.15;
  const k_eff = k_A * Math.exp(-Ea / R_GAS * (1 / T_K - 1 / T_ref_K_sei));
  const d_delta = k_eff / (2 * (sei_state.delta_SEI + 1e-6));
  sei_state.delta_SEI += d_delta * dt;
  sei_state.t_elapsed += dt;

  // SEI 저항 증가
  const R_sei_max = params.R_sei_max || 0.05;
  sei_state.R_sei = Math.min(R_sei_max,
    (params.R_sei_init || 0.001) + sei_state.delta_SEI * 0.02);

  // Li-plating 검출: 충전 중 음극 전위 < threshold
  const Q_cell = (params.Q_nom || 200) / (params.bms_cell_count_p || 4);
  const V_cell = (battery_state.V_terminal || params.V_dc) / (params.bms_cell_count_s || 96);
  const I_cell = I_batt / (params.bms_cell_count_p || 4);
  if (I_cell < -0.5) {  // 충전
    const phi_anode = V_cell - Math.abs(I_cell) * sei_state.R_sei;
    if (phi_anode < (params.k_plating_threshold || 0.0)) {
      sei_state.li_plating_count++;
      // [Fix-SEI-Separate] 플레이팅 손실을 Q_loss_plating으로 분리
      // 기존: Q_loss_sei에 중첩 누산 → SEI 캘린더 손실과 혼용
      sei_state.Q_loss_plating = (sei_state.Q_loss_plating || 0) + 0.001;
    }
  }

  // SEI 캘린더 손실만 Q_loss_sei에 누산 (플레이팅 경로 분리)
  sei_state.Q_loss_sei += d_delta * dt * 0.5;

  return sei_state;
}

// ── initRainflowState ──
/**
 * Rainflow 사이클 카운팅 초기 상태
 *
 * ASTM E1049 표준 알고리즘 기반.
 * SOC 반전점(극값)을 스택에 쌓고,
 * 3점 규칙으로 half-cycle / full-cycle 을 추출한다.
 *
 * @param {object} params
 * @returns {object} rainflow 상태
 */
function initRainflowState(params) {
  return {
    peaks:          [],      // 반전점 스택 [SOC 값]
    half_cycles:    [],      // 추출된 half-cycle [{range, mean, count:0.5}]
    full_cycles:    [],      // 추출된 full-cycle  [{range, mean, count:1.0}]
    soc_prev:       null,    // 직전 SOC (반전 감지용)
    direction_prev: 0,       // 직전 방향 (+1 상승 / -1 하강 / 0 미결정)
    total_damage:   0,       // 누적 피로 손상 D = Σ(n_i / N_f_i)
    cycle_count_rf: 0,       // 총 사이클 수 (full=1, half=0.5 가중)
  };
}

/**
 * SOC 반전점 감지 — 방향이 바뀌는 순간의 직전 값을 극값으로 등록
 *
 * @param {object} rf_state  - rainflow 상태 (initRainflowState 반환)
 * @param {number} soc       - 현재 SOC
 * @returns {boolean}        - 극값이 추가됐으면 true
 */
function rainflowAddPoint(rf_state, soc) {
  if (rf_state.soc_prev === null) {
    rf_state.soc_prev = soc;
    // [Fix-Rainflow] 첫 번째 점도 peaks에 등록 (시작 극값)
    rf_state.peaks.push(soc);
    return false;
  }
  const dir = soc > rf_state.soc_prev ? 1 : soc < rf_state.soc_prev ? -1 : 0;
  if (dir === 0) { rf_state.soc_prev = soc; return false; }

  const reversed = dir !== 0 && rf_state.direction_prev !== 0 && dir !== rf_state.direction_prev;
  if (reversed) {
    // 방향 전환 → 직전 값을 반전점으로 등록
    rf_state.peaks.push(rf_state.soc_prev);
    // ASTM 3점 추출 규칙
    _rainflowExtract(rf_state);
  }
  rf_state.direction_prev = dir;
  rf_state.soc_prev = soc;
  return reversed;
}

/**
 * ASTM E1049 3점 규칙 사이클 추출 (내부 함수)
 * 스택에 4개 이상 쌓이면 중간 두 범위를 비교해 사이클 추출
 */
function _rainflowExtract(rf_state) {
  const P = rf_state.peaks;
  // 스택 크기 4 이상일 때 반복 추출
  while (P.length >= 4) {
    const n = P.length;
    // 끝에서 4개: P[n-4], P[n-3], P[n-2], P[n-1]
    const X = Math.abs(P[n-1] - P[n-2]);  // 최신 범위
    const Y = Math.abs(P[n-2] - P[n-3]);  // 이전 범위
    const Z = Math.abs(P[n-3] - P[n-4]);  // 그 이전 범위
    if (Y <= X && Y <= Z) {
      // 중간 반전점 pair가 사이클 하나를 구성
      const range = Y;
      const mean  = (P[n-2] + P[n-3]) / 2;
      rf_state.full_cycles.push({ range, mean, count: 1.0 });
      rf_state.cycle_count_rf += 1.0;
      // 중간 두 점 제거
      P.splice(n-3, 2);
    } else {
      break;  // 더 이상 추출 불가
    }
  }
}

/**
 * Rainflow 잔여 스택 처리 — 시뮬 종료 시 호출
 * 남은 반전점을 half-cycle로 등록
 */
function rainflowFinalize(rf_state) {
  // [Fix-Rainflow] 마지막 soc_prev를 종료 극값으로 등록
  if (rf_state.soc_prev !== null) {
    rf_state.peaks.push(rf_state.soc_prev);
  }
  const P = rf_state.peaks;
  for (let i = 0; i + 1 < P.length; i++) {
    const range = Math.abs(P[i+1] - P[i]);
    const mean  = (P[i+1] + P[i]) / 2;
    rf_state.half_cycles.push({ range, mean, count: 0.5 });
    rf_state.cycle_count_rf += 0.5;
  }
  rf_state.peaks = [];
}

/**
 * Rainflow → 피로 손상 계산
 *
 * Miner 선형 피로 누적: D = Σ(n_i / N_f_i)
 * N_f(DOD) = N_ref × (DOD_ref / DOD)^k_dod  (멱함수 수명 모델)
 *
 * @param {object} rf_state   - rainflow 상태
 * @param {object} params     - 차량 파라미터
 * @returns {number}          - 누적 손상 지수 D (D≥1: EOL)
 */
function rainflowDamage(rf_state, params) {
  const N_ref   = params.rf_N_ref   || 3000;   // 기준 DOD에서의 수명 사이클
  const DOD_ref = params.rf_DOD_ref || 0.8;    // 기준 DOD
  const k_dod   = params.rf_k_dod   || 1.5;   // 멱지수 (DOD 민감도)
  const k_mean  = params.rf_k_mean  || 0.3;   // 평균 SOC 보정 (고SOC→더 빠른 열화)

  let D = 0;
  const allCycles = [
    ...rf_state.full_cycles,
    ...rf_state.half_cycles,
  ];
  for (const c of allCycles) {
    const DOD = Math.max(1e-6, c.range);
    // DOD 기반 수명: N_f = N_ref × (DOD_ref/DOD)^k_dod
    const N_f = N_ref * Math.pow(DOD_ref / DOD, k_dod);
    // 평균 SOC 보정: 고 SOC에서 열화 가속
    const f_mean = 1 + k_mean * Math.max(0, c.mean - 0.5);
    D += c.count / (N_f * f_mean);
  }
  rf_state.total_damage = D;
  return D;
}

/**
 * updateSOH에 Rainflow 손상을 통합
 * 기존 updateSOH를 호출하고 Rainflow 손상을 추가 반영
 *
 * @param {object} soh_state    - SOH 상태
 * @param {object} rf_state     - Rainflow 상태
 * @param {object} battery_state
 * @param {number} I_batt
 * @param {number} dt
 * @param {object} params
 * @returns {object} 갱신된 soh_state
 */
function updateSOHWithRainflow(soh_state, rf_state, battery_state, I_batt, dt, params) {
  // 기존 SOH 갱신
  const soh_new = updateSOH(soh_state, battery_state, I_batt, dt, params);
  if (!params.rainflow_enable || !rf_state) return soh_new;

  // SOC 반전점 추가
  rainflowAddPoint(rf_state, battery_state.SOC);

  // Rainflow 피로 손상 계산 (매 스텝 전체 사이클 재계산은 비효율 → 10사이클마다)
  // [Fix-Rainflow-FP] 기존 조건 (rf_state.cycle_count_rf % 10) < 1 은
  // cycle_count_rf가 0.5 단위로 증분하고 부동소수점 오차가 누적되면
  // 의도치 않게 항상 참이 되거나 트리거를 놓칠 수 있음.
  // → Math.floor로 정수화 후 비교, 첫 스텝 오호출 방지를 위해 > 0 조건 추가.
  const _rf_count_floor = Math.floor(rf_state.cycle_count_rf);
  if (_rf_count_floor > 0 && _rf_count_floor % 10 === 0 &&
      _rf_count_floor !== (rf_state._last_damage_at || 0)) {
    rf_state._last_damage_at = _rf_count_floor;
    const D = rainflowDamage(rf_state, params);
    // Miner 손상 → SOH 반영
    // D=0: 손상 없음, D=1: EOL (SOH=0.8 대응)
    const cap_fade_from_rf = Math.min(0.5, D * 0.20);  // D=1 → 20% 용량 손실
    soh_new.SOH = Math.max(0.5, Math.min(soh_new.SOH, 1.0 - cap_fade_from_rf));
    soh_new.rainflow_damage = D;
    soh_new.rainflow_cycles = rf_state.cycle_count_rf;
  }
  return soh_new;
}

// ── liPlatingDetect ──
/**
 * 리튬 플레이팅 감지 — [B-L1] 온도 의존 임계 전압 개선
 *
 * 기존: 고정 임계값 V_anode_threshold=0.0V — 온도 불문 동일 판정
 * 개선: 임계값 V_thr(T) — 저온에서 더 높은 전압에서도 플레이팅 발생
 *   실험식: V_thr(T) = V_thr0 + k_thr × max(0, T_ref − T_cell)
 *     V_thr0 ≈ 0.0 V (25°C 기준)
 *     k_thr  ≈ 0.003 V/°C  (저온 이온 이동도 감소로 인한 여유 상실)
 *
 * 예: T=0°C → V_thr = 0 + 0.003×25 = 0.075 V  (0.075V 아래에서 플레이팅)
 *     T=25°C → V_thr = 0.0 V  (기존 동작 유지)
 *     T=-10°C → V_thr = 0.105 V  (극저온 고위험)
 *
 * 참고: Petzl & Danzer (2014), "Nondestructive detection … lithium plating"
 *        J. Power Sources 254, 80–87
 */
function liPlatingDetect(battery_state, I_batt, params) {
  if (!params.li_plating_enable) return { plating: false, phi_anode: null };
  const V_cell = battery_state.V_terminal / (params.bms_cell_count_s || 96);
  const R_int  = (params.R0_base || 0.020) + (params.R1_base || 0.015);
  const I_cell = I_batt / (params.bms_cell_count_p || 4);
  // [Fix-liPlating-Sign] Math.abs로 올바른 전압강하 반영
  const phi_anode = V_cell - Math.abs(I_cell) * R_int;

  // [B-L1] 온도 의존 임계값
  const T_cell   = battery_state.T_cell ?? (params.T_amb ?? 25);
  const T_ref    = params.T_ref || 25;
  const V_thr0   = params.V_anode_threshold || 0.0;       // 기준 임계값 [V]
  const k_thr    = params.k_plating_temp    || 0.003;     // 온도 계수 [V/°C]
  // 저온에서 임계값 상승 (더 엄격한 판정), 고온은 기준값 유지
  const V_threshold = V_thr0 + k_thr * Math.max(0, T_ref - T_cell);

  const plating  = (I_cell < 0) && (phi_anode < V_threshold);
  const margin   = phi_anode - V_threshold;   // 양수: 안전 마진, 음수: 플레이팅 진입

  return {
    plating,
    phi_anode:   +phi_anode.toFixed(4),
    V_threshold: +V_threshold.toFixed(4),     // [B-L1] 온도 의존 임계값
    margin:      +margin.toFixed(4),
    V_cell:      +V_cell.toFixed(4),
    I_cell:      +I_cell.toFixed(2),
    T_cell:      +T_cell.toFixed(1),
    risk_level:  plating ? (phi_anode < V_threshold - 0.05 ? 'HIGH' : 'LOW') : 'NONE',
  };
}

// ── initBMSCells ──
/**
 * BMS 셀 상태 배열 초기화
 *
 * ns×np 격자 구조로 셀 배열을 초기화한다.
 * 실제 팩 제조 편차를 모사하기 위해 초기 전압에 ±10mV 랜덤 분산을 적용한다.
 *
 * @param {object} params
 *   params.bms_cell_count_s - 직렬 셀 수 (기본 96)
 *   params.bms_cell_count_p - 병렬 셀 수 (기본 4)
 * @returns {{ cells: Array, faults: Array, contactor_on: boolean, balance_energy_Wh: number }}
 */
function initBMSCells(params) {
  const ns = params.bms_cell_count_s || 96;
  const np = params.bms_cell_count_p || 4;
  const V_nom = 3.7;
  const cells = [];
  for (let i = 0; i < ns; i++) {
    const V_spread = (Math.random() - 0.5) * 0.020; // ±10mV initial spread
    for (let j = 0; j < np; j++) {
      cells.push({
        id: `${i}s${j}p`,
        V: +(V_nom + V_spread).toFixed(4),
        SOH: +(0.98 + (Math.random() - 0.5) * 0.02).toFixed(4),
        T: 25,
        balancing: false,
      });
    }
  }
  return { cells, faults: [], contactor_on: true, balance_energy_Wh: 0 };
}

// ── bmsStep ──
/**
 * BMS 1스텝 실행 — 셀 밸런싱, 과온/과전압 보호, 충전 전류 제한
 *
 * 매 dt마다 호출되며 다음을 수행한다:
 *   1. 온도 기반 충전 전류 제한 (과온 → I_charge_lim = 0)
 *   2. 셀별 능동 밸런싱: |ΔV| > threshold 셀에 전류 방향으로 조정
 *      - 밸런싱 전류로 인한 셀 전압 변화(dV)를 OCV 기울기 기반으로 계산
 *   3. 과전압(OV)/저전압(UV)/과전류(OC) 폴트 감지
 *   4. bms_fault_latch=true 시 컨택터 OFF
 *
 * @param {object} bms_state - initBMSCells 반환 상태 (내부 수정됨)
 * @param {number} I_batt    - 팩 전류 [A]
 * @param {number} T_cell    - 배터리 온도 [°C]
 * @param {number} dt        - 타임스텝 [s]
 * @param {object} params    - 차량 파라미터 (bms_balance_threshold, bms_cell_v_max 등)
 * @returns {{ faults, n_balancing, contactor_on, I_charge_lim, P_balance_loss_Wh, V_avg, V_min, V_max }}
 */
function bmsStep(bms_state, I_batt, T_cell, dt, params) {
  const V_avg = bms_state.cells.reduce((s,c) => s+c.V, 0) / bms_state.cells.length;
  const V_threshold = params.bms_balance_threshold || 0.010;
  const I_bal = params.bms_balance_current || 1.5;
  const V_max_limit = params.bms_cell_v_max || 4.20;
  const V_min_limit = params.bms_cell_v_min || 2.80;
  const T_hot = params.bms_t_hot_cutoff || 55;
  const T_cold = params.bms_t_cold_derate || 5;
  const I_chg_max = params.bms_i_charge_max || 200;
  let P_balance_loss = 0;
  let faults = [];

  // Temperature-based charge current limit
  let I_charge_lim = I_chg_max;
  if (T_cell > T_hot) {
    I_charge_lim = 0;
    faults.push({ code: 'OT_CHG', msg: `과온 충전 차단: T=${T_cell.toFixed(1)}°C` });
  } else if (T_cell < T_cold) {
    const derate = Math.max(0.1, (T_cell - T_cold + 10) / 10);
    I_charge_lim = I_chg_max * derate;
  }

  // Active balancing + fault diagnosis per cell
  for (const cell of bms_state.cells) {
    const dV = cell.V - V_avg;
    // Balancing
    if (Math.abs(dV) > V_threshold && params.bms_balance_mode !== 'off') {
      cell.balancing = true;
      const I_dir = dV > 0 ? I_bal : -I_bal * 0.5; // active: bi-directional
      P_balance_loss += Math.abs(I_dir * cell.V) * dt / 3600; // Wh
      // [Fix-BMS-Balancing] 밸런싱 전류로 인한 셀 전압 업데이트.
      // 기존 코드는 cell.balancing 플래그만 세우고 cell.V를 변경하지 않아
      // 다음 스텝에서도 동일한 ΔV가 유지되어 밸런싱이 수렴하지 않았음.
      // 수정: I_dir [A] × dt [s] / 3600 → dAh, dSOC = dAh / Q_cell,
      //   dV ≈ dSOC × OCV_slope (~0.5 V/SOC 중간점 근사).
      const Q_cell = (params.Q_nom || 200) / (params.bms_cell_count_p || 4);
      const dSoc   = (I_dir * dt / 3600) / Q_cell;   // + = 방전(고전압 셀), - = 충전(저전압 셀)
      // [Fix-BMS-OCV-Slope] dOCV_dSOC() 기반 동적 기울기 사용
      //   기존: 0.5 V/SOC 고정 근사 (NMC 중간점만 유효)
      //   수정: 현재 셀 전압으로 SOC를 역산한 뒤 OCV-LUT 수치 미분 적용
      //         → SOC 0%~100% 전 구간에서 실제 OCV 곡률 반영
      const soc_est  = Math.max(0, Math.min(1, (cell.V - 2.8) / (4.2 - 2.8)));
      const ocv_slope = dOCV_dSOC(soc_est, null, null); // V/SOC (LUT 수치 미분, 기본 LUT)
      cell.V -= dSoc * ocv_slope;
    } else {
      cell.balancing = false;
    }
    // Fault detection
    if (cell.V > V_max_limit) faults.push({ code: 'OV', cell: cell.id, V: cell.V });
    if (cell.V < V_min_limit) faults.push({ code: 'UV', cell: cell.id, V: cell.V });
    if (Math.abs(I_batt) > (params.I_max || 500)) faults.push({ code: 'OC', I: I_batt });
  }

  // Contactor OFF on latched fault
  if (faults.length > 0 && params.bms_fault_latch) {
    if (faults.some(f => ['OV','UV','OT_CHG','OC'].includes(f.code))) {
      bms_state.contactor_on = false;
    }
  }

  bms_state.faults = faults;
  bms_state.balance_energy_Wh += P_balance_loss;
  bms_state.I_charge_lim = +I_charge_lim.toFixed(0);
  const n_balancing = bms_state.cells.filter(c => c.balancing).length;
  return {
    faults, n_balancing, contactor_on: bms_state.contactor_on,
    I_charge_lim, P_balance_loss_Wh: +bms_state.balance_energy_Wh.toFixed(4),
    V_avg: +V_avg.toFixed(4),
    V_min: +Math.min(...bms_state.cells.map(c=>c.V)).toFixed(4),
    V_max: +Math.max(...bms_state.cells.map(c=>c.V)).toFixed(4),
  };
}

// ── initCCCVState ──
/**
 * CC-CV 충전 프로토콜 초기 상태
 *
 * 충전 단계:
 *   PRE   — 예비 충전 (T<T_min 또는 SOC<SOC_pre 일 때 저전류)
 *   CC    — 정전류 (Constant Current): SOC < SOC_cv 까지
 *   CV    — 정전압 (Constant Voltage): V_cell = V_max, 전류 감소
 *   DONE  — 충전 완료 (I < I_cutoff 또는 SOC ≥ SOC_max)
 *   FAULT — 결함 (OT, OV, UV 등)
 *
 * @param {object} params
 * @returns {object} CC-CV 상태
 */
function initCCCVState(params) {
  return {
    phase:       'PRE',     // 'PRE' | 'CC' | 'CV' | 'DONE' | 'FAULT'
    I_cmd:       0,         // 현재 충전 전류 지령 [A] (음수 = 충전)
    V_cmd:       params.bms_v_cell_max || 4.20,  // CV 목표 전압 [V/cell]
    E_charged:   0,         // 누적 충전 에너지 [Wh]
    Ah_charged:  0,         // 누적 충전 용량 [Ah]
    t_elapsed:   0,         // 충전 경과 시간 [s]
    cv_v_integral: 0,       // [P-9-Fix] CV PI 적분 누산 [V·s]
    fault_code:  null,
  };
}

/**
 * ccCVStep — CC-CV 충전 1스텝
 *
 * 매 dt마다 호출. 배터리 상태·온도를 보고 충전 전류를 결정한다.
 * 반환된 I_cmd를 battery2RCModelWithSEI에 전달하면 된다.
 *
 * @param {object} cccv_state   - CC-CV 상태 (initCCCVState 반환값)
 * @param {object} battery_state - 2RC 배터리 상태 { SOC, V_terminal, T_cell }
 * @param {number} dt           - 시간 스텝 [s]
 * @param {object} params       - 차량 파라미터
 * @returns {object}            - 갱신된 cccv_state
 */
function ccCVStep(cccv_state, battery_state, dt, params) {
  const SOC      = battery_state.SOC;
  const T_cell   = battery_state.T_cell  ?? (params.T_amb || 25);
  const V_pack   = battery_state.V_terminal ?? (params.V_dc || 400);
  const n_s      = params.bms_cell_count_s || 96;
  const V_cell   = V_pack / n_s;             // 셀 전압

  // ── 파라미터 기본값 ────────────────────────────────────────────
  // [Fix-CCCV-Sign] 입력이 양수(200) 또는 음수(-200) 모두 허용, 내부는 음수(충전) 통일
  const I_cc_raw     = params.cccv_I_cc     ?? params.bms_i_charge_max ?? 200;
  const I_cc         = -Math.abs(I_cc_raw);                              // 항상 음수
  const I_pre        = -Math.abs(params.cccv_I_pre    || Math.abs(I_cc) * 0.1);
  const I_cutoff     = -Math.abs(params.cccv_I_cutoff || Math.abs(I_cc) * 0.05);
  const V_cv     = params.bms_v_cell_max  || 4.20;                             // CV 전압 [V/cell]
  const SOC_cv   = params.cccv_soc_cv     || 0.85;                             // CC→CV 전환 SOC
  const SOC_pre  = params.cccv_soc_pre    || 0.05;                             // PRE→CC 전환 SOC
  const SOC_max  = params.bms_soc_charge_cutoff || 0.995;
  const T_min    = params.bms_t_charge_min || 0;                               // 충전 허용 최저 온도
  const T_max    = params.bms_t_charge_max || 45;                              // 충전 허용 최고 온도
  const T_hot    = params.bms_t_hot_cutoff || 55;

  // ── FAULT 감지 ────────────────────────────────────────────────
  if (T_cell > T_hot) {
    cccv_state.phase = 'FAULT';
    cccv_state.fault_code = 'OT';
    cccv_state.I_cmd = 0;
    return cccv_state;
  }
  if (V_cell > V_cv * 1.05) {
    cccv_state.phase = 'FAULT';
    cccv_state.fault_code = 'OV';
    cccv_state.I_cmd = 0;
    return cccv_state;
  }
  if (cccv_state.phase === 'FAULT') { cccv_state.I_cmd = 0; return cccv_state; }

  // ── DONE ──────────────────────────────────────────────────────
  if (cccv_state.phase === 'DONE') { cccv_state.I_cmd = 0; return cccv_state; }

  // ── 온도 기반 전류 디레이팅 ────────────────────────────────────
  let derate = 1.0;
  if (T_cell < T_min + 5) {
    // 저온 디레이팅: T_min에서 10%, T_min+5에서 100%
    derate = Math.max(0.10, 0.10 + 0.90 * (T_cell - T_min) / 5);
  } else if (T_cell > T_max) {
    derate = Math.max(0.20, 1.0 - 0.10 * (T_cell - T_max));
  }
  const I_cc_derated  = I_cc  * derate;
  const I_pre_derated = I_pre * derate;

  // ── 상태 전환 로직 ────────────────────────────────────────────
  switch (cccv_state.phase) {

    case 'PRE':
      // PRE: 저온 or 초저SOC → 저전류 예비 충전
      if (T_cell >= T_min && SOC >= SOC_pre) {
        cccv_state.phase = 'CC';
        // fall through → CC 처리
      } else {
        cccv_state.I_cmd = I_pre_derated;
        break;
      }
      // fall through

    case 'CC':
      // CC: SOC < SOC_cv 이고 셀전압 < V_cv 이면 정전류
      if (SOC >= SOC_max || V_cell >= V_cv) {
        cccv_state.phase = 'DONE';
        cccv_state.I_cmd = 0;
      } else if (SOC >= SOC_cv || V_cell >= V_cv * 0.999) {
        // CV 전환
        cccv_state.phase = 'CV';
        cccv_state.I_cmd = I_cc_derated;  // 첫 스텝은 CC 전류 그대로
      } else {
        cccv_state.I_cmd = I_cc_derated;
      }
      break;

    case 'CV': {
      // [P-9-Fix] CV phase: P-only → PI 제어기로 교체
      // 이전(P-only): I_cv = -(Kp * V_err)
      //   → 정상상태에서 V_err > 0 잔류 (저항 전압강하 미보상)
      //   → I_cmd가 I_cutoff에 도달하지 못해 SOC 조기 종료 (충전 미달)
      // 수정(PI): I_cv = -(Kp * V_err + Ki * ∫V_err dt)
      //   → 적분항이 정상상태 오프셋을 0으로 수렴
      //   → CV 전류 테이퍼가 규정대로 I_cutoff까지 완주
      //
      // 파라미터:
      //   cccv_Kp [A/V]   : 비례 게인, 기본 50  (0.01 V 오차 → 0.5 A)
      //   cccv_Ki [A/V·s] : 적분 게인, 기본 5   (과적분 방지 상한: |I_cc|/2)
      const V_err   = V_cv - V_cell;              // 목표-현재 [V], 양수 = 여유
      const Kp_cv   = params.cccv_Kp || 50;       // 비례 게인 [A/V]
      const Ki_cv   = params.cccv_Ki || 5;        // 적분 게인 [A/(V·s)]
      const i_lim   = Math.abs(I_cc) / 2;         // 적분 누산 상한 [A] — anti-windup

      // 적분 누산 (anti-windup: 포화 방향 동일 시 동결)
      const raw_integral = cccv_state.cv_v_integral + V_err * dt;
      const new_integral = Math.max(-i_lim / Ki_cv, Math.min(i_lim / Ki_cv, raw_integral));

      let I_cv = -(Kp_cv * Math.max(0, V_err) + Ki_cv * Math.max(0, new_integral));
      I_cv = Math.min(0, Math.max(I_cutoff * 1.5, I_cv));  // 0 ≥ I_cv ≥ I_cutoff*1.5
      I_cv = Math.max(I_cv, I_cc_derated);                 // CC 전류 상한 클램프

      // anti-windup: 출력 포화 & 적분 증가 방향 동일이면 적분 동결
      cccv_state.cv_v_integral = (I_cv <= I_cutoff * 1.5 && V_err > 0)
        ? cccv_state.cv_v_integral   // 포화 동결
        : new_integral;

      cccv_state.I_cmd = I_cv;
      // 종료 조건: 전류 < 차단 전류 또는 SOC 완충
      if (cccv_state.I_cmd >= I_cutoff || SOC >= SOC_max) {
        cccv_state.phase = 'DONE';
        cccv_state.I_cmd = 0;
      }
      break;
    }

    default:
      cccv_state.I_cmd = 0;
  }

  // ── 누적 통계 ─────────────────────────────────────────────────
  const I_act  = Math.abs(cccv_state.I_cmd);
  const V_safe = Math.max(100, Math.min(1000, V_pack));  // [Fix] 물리 범위 클램프
  cccv_state.t_elapsed  += dt;
  cccv_state.Ah_charged += I_act * dt / 3600;
  cccv_state.E_charged  += I_act * V_safe * dt / 3600;

  return cccv_state;
}

// ── initCellImbalanceState ──
/**
 * 배터리 셀 불균형(Cell Imbalance) 상태 초기화
 *
 * 실 배터리 팩은 셀 간 제조 편차, 온도 분포, 저항 편차로 인해 SOC 분포가
 * 점차 벌어짐. 불균형이 커지면 약셀(weakest cell)이 먼저 OV/UV에 도달해
 * 유효 팩 용량이 줄고 Thermal Runaway 위험이 높아짐.
 *
 * 모델 개요 (파라미터화된 통계 모델, 셀 개별 적분 없이 σ_SOC EMA로 근사):
 *   - 초기 분산: σ_SOC_0 (제조 편차)
 *   - 사이클당 분산 증가: Δσ_cycle × (SOC - 0.5)² (편향 충방전 시 악화)
 *   - 밸런싱 시 분산 감소: σ_SOC × (1 - k_bal × dt)
 *   - 유효 용량 손실: f_imb = max(0, 1 - k_imb × σ_SOC)
 */
function initCellImbalanceState(params) {
  return {
    sigma_SOC: params.cell_imbalance_init || 0.005,  // 초기 SOC 표준편차 (무차원, 0.5% 초기 편차)
    worst_margin: 1.0,                                // 약셀 여유율 (1 = 정상)
    cycles_imb: 0,
  };
}

/**
 * 셀 불균형 업데이트 — SOC 분산 추적 + 유효 용량 감소 계산
 *
 * @param {object} imb_state   - initCellImbalanceState() 반환값
 * @param {number} SOC         - 팩 평균 SOC
 * @param {number} I_batt      - 팩 전류 [A] (양수=방전)
 * @param {boolean} balancing  - 현재 밸런싱 동작 여부 (bmsStep 결과)
 * @param {number} dt          - 시간 스텝 [s]
 * @param {object} params
 *   params.cell_imbalance_enable  : 활성화 플래그
 *   params.cell_imbalance_k_cyc   : 사이클당 σ 증가율 (기본 2e-5 /[full cycle])
 *   params.cell_imbalance_k_bal   : 밸런싱 시 σ 감소율 [/s] (기본 5e-4)
 *   params.cell_imbalance_k_cap   : σ → 용량 손실 변환계수 (기본 8.0)
 *   params.Q_nom                  : 공칭 용량 [Ah] (사이클 기준)
 * @returns {{ imb_state, f_imbalance, sigma_SOC }}
 */
function updateCellImbalance(imb_state, SOC, I_batt, balancing, dt, params) {
  if (!params.cell_imbalance_enable) {
    return { imb_state, f_imbalance: 1.0, sigma_SOC: imb_state.sigma_SOC };
  }
  const k_cyc = params.cell_imbalance_k_cyc || 2e-5;  // σ 증가율 [/Ah]
  const k_bal = params.cell_imbalance_k_bal || 5e-4;  // 밸런싱 시 σ 감소율 [/s]
  const k_cap = params.cell_imbalance_k_cap || 8.0;   // σ → 용량 손실 계수
  const Q_nom = params.Q_nom || 200;

  // 충방전으로 인한 σ 증가 — 편향 SOC(>0.8 or <0.2)에서 더 빠르게 벌어짐
  const dAh = Math.abs(I_batt) * dt / 3600;
  const bias = 1 + 3 * Math.pow(Math.abs(SOC - 0.5) * 2, 2); // SOC 50% 중심 편향 계수
  imb_state.sigma_SOC += k_cyc * dAh / Q_nom * bias;

  // 밸런싱 시 σ 감소 (패시브/액티브 모두 동일 계수 적용)
  if (balancing) {
    imb_state.sigma_SOC *= Math.max(0, 1 - k_bal * dt);
  }

  // σ 상한 클램프 — 현실적 최대 편차 15%
  imb_state.sigma_SOC = Math.max(1e-4, Math.min(0.15, imb_state.sigma_SOC));

  // 약셀 여유율: 가장 낮은 SOC 셀이 OV/UV에 도달하는 마진 (1σ 하한 근사)
  const soc_min_est = Math.max(0, SOC - 3 * imb_state.sigma_SOC); // 3σ 하한
  imb_state.worst_margin = SOC > 0.01 ? soc_min_est / SOC : 1.0;

  // 유효 용량 감소 계수: 불균형이 클수록 팩 유효 용량 감소
  //   f_imb = max(0.5, 1 − k_cap × σ_SOC)
  //   σ=0 → f=1.0 (완전 균형), σ=0.05 → f=0.6, σ=0.15 → f=0.5 (하한)
  const f_imbalance = Math.max(0.50, 1.0 - k_cap * imb_state.sigma_SOC);

  return {
    imb_state,
    f_imbalance: +f_imbalance.toFixed(4),
    sigma_SOC:   +imb_state.sigma_SOC.toFixed(5),
    worst_margin: +imb_state.worst_margin.toFixed(4),
    risk_level:  imb_state.sigma_SOC > 0.05 ? 'HIGH' :
                 imb_state.sigma_SOC > 0.02 ? 'MEDIUM' : 'LOW',
  };
}

// ── computeDCLinkSpike ──
/**
 * [B-L2] DC-링크 전압 스파이크 — 회생제동 급차단 과도 전압 모델
 *
 * 회생제동 중 급제동 해제(예: ABS 작동, 드라이버 브레이크 해제)가 발생하면
 * 인버터가 회생 전류를 급격히 차단함. 이때 DC 링크 인덕턴스(L_dc)에서
 * 전압 스파이크가 발생: V_spike = L_dc × (dI/dt)
 *
 * 이 스파이크가 인버터 IGBT 내압(V_CE_max)이나 커패시터 정격을 초과하면
 * 소자 파손 위험 — 과전압 보호 임계를 파라미터로 정의.
 *
 * 완충 커패시터 전압 응답 (1차 RC 모델):
 *   C_dc × dV/dt = I_spike − I_load
 *   τ = R_dc × C_dc  (시정수)
 *   V_peak ≈ V_dc + L_dc × |dI_regen/dt|  (무부하 근사 최악 케이스)
 *
 * @param {number} I_regen_prev  - 이전 스텝 회생 전류 [A] (양수)
 * @param {number} I_regen_now   - 현재 스텝 회생 전류 [A]
 * @param {number} V_dc_now      - 현재 DC-링크 전압 [V]
 * @param {number} dt            - 타임스텝 [s]
 * @param {object} params
 *   params.L_dc            : DC 링크 인덕턴스 [H] (기본 5µH = 5e-6)
 *   params.C_dc            : DC 링크 커패시턴스 [F] (기본 2000µF = 2e-3)
 *   params.R_dc_link        : DC 링크 등가 직렬 저항 [Ω] (기본 0.005)
 *   params.V_dc_spike_limit : 허용 최대 DC 전압 [V] (기본 V_dc × 1.15)
 *
 * @returns {{ V_spike, dI_dt, overvoltage, V_limit, margin_pct }}
 *
 * 참고: Mohan, Undeland & Robbins "Power Electronics" §13.4; SAE J2907
 */
function computeDCLinkSpike(I_regen_prev, I_regen_now, V_dc_now, dt, params) {
  const L_dc    = params.L_dc          || 5e-6;    // [H] DC링크 인덕턴스
  const C_dc    = params.C_dc          || 2e-3;    // [F] DC링크 커패시턴스
  const R_dc    = params.R_dc_link     || 0.005;   // [Ω] 등가 직렬저항
  const V_dc    = V_dc_now || params.V_dc || 400;
  const V_limit = params.V_dc_spike_limit || V_dc * 1.15;  // 15% 마진 기본값

  const dt_safe  = Math.max(dt, 1e-6);
  const dI_dt    = (I_regen_prev - I_regen_now) / dt_safe;   // 전류 감소율 [A/s]

  // 인덕턴스 전압 스파이크 (순간 최악 케이스)
  const V_L_spike = L_dc * Math.abs(dI_dt);

  // 커패시터가 스파이크를 흡수한 후의 실효 피크 전압 (1차 감쇠 근사)
  // 커패시터 임피던스: |Z_C| = 1 / (ω × C_dc), ω ≈ 1 / (dt = 스파이크 지속 시간)
  // 단순화: V_peak = V_dc + V_L × exp(−dt_spike / τ)
  // dt_spike ≈ L/R (인덕턴스 시정수), τ = R×C (RC 시정수)
  const tau_L  = R_dc > 0 ? L_dc / R_dc : 1e-3;
  const tau_RC = R_dc * C_dc;
  const decay  = Math.exp(-Math.max(tau_L, dt_safe) / Math.max(tau_RC, 1e-9));
  const V_spike = V_dc + V_L_spike * (1 - decay);  // 커패시터 흡수 후 피크 전압 [V]

  const overvoltage  = V_spike > V_limit;
  const margin_pct   = +((V_limit - V_spike) / V_limit * 100).toFixed(2);

  return {
    V_spike:     +V_spike.toFixed(2),
    dI_dt:       +dI_dt.toFixed(1),
    V_L_spike:   +V_L_spike.toFixed(2),
    overvoltage,
    V_limit:     +V_limit.toFixed(1),
    margin_pct,
    risk_level:  overvoltage ? (margin_pct < -5 ? 'HIGH' : 'LOW') : 'NONE',
  };
}


// ══════════════════════════════════════════════════════════════════
// [Sprint-4] computeParallelStringImbalance — 병렬 스트링 불균형 모델
// ISO 12405 / USABC 기반 N개 병렬 스트링 간 SOC·전류 편차 추적
//
// 단일 스트링 updateCellImbalance: 직렬 셀 간 SOC 편차(σ_SOC)만 추적
// 이 함수: 병렬 스트링 간 전류·SOC 불균형 (실차 팩 모델에 필수)
//
// 모델 개요:
//  · 각 스트링은 독립 내부저항 R_s[i] 보유 (제조 편차 ±σ_R)
//  · 팩 단자 전압 공유 → 내부저항 차이 → 전류 분배 비율 결정
//  · 전류 불균형 → SOC 발산 → 약 스트링 조기 종료 → 용량 손실
//
// 입력:
//  n_strings  : 병렬 스트링 수 (정수, ≥ 2)
//  soc_avg    : 팩 평균 SOC (0~1)
//  I_total    : 전체 팩 전류 [A] (양수=방전)
//  dt         : 시간 스텝 [s]
//  state      : initParallelStringState() 반환값
//  params     : 아래 키 지원
//    R_batt          : 단일 스트링 기준 내부저항 [Ω]
//    sigma_R_string  : 스트링 간 저항 표준편차 (비율, 기본 0.03 = ±3%)
//    Q_string_Ah     : 스트링 공칭 용량 [Ah] (기본 100 Ah)
//    cell_imbalance_k_cap : σ_SOC → 용량 손실 계수 (기본 8.0)
//
// 반환: { state, f_pack, string_soc, string_I, sigma_soc_str, weakest_idx }
// ══════════════════════════════════════════════════════════════════
/**
 * 병렬 스트링 불균형 상태 초기화
 *
 * n_strings개 병렬 스트링 각각의 내부저항을 결정론적 균등 분포로 초기화한다.
 * 가우시안 랜덤 대신 균등 분포를 사용해 시뮬레이션 재현성을 보장한다.
 * 편차 범위: R_base × [1 − σ_R, 1 + σ_R]
 *
 * @param {number} n_strings  - 병렬 스트링 수 (≥ 2)
 * @param {object} [params]
 *   params.sigma_R_string - 스트링 간 저항 표준편차 비율 (기본 0.03 = ±3%)
 *   params.R_batt         - 기준 스트링 내부저항 [Ω] (기본 0.08)
 *   params.soc_init       - 초기 스트링 SOC (기본 0.8)
 * @returns {{ n, R_strings, soc_str, cycles_str, t_total }}
 */
export function initParallelStringState(n_strings, params = {}) {
  const sigma_R = params.sigma_R_string ?? 0.03;
  const R_base  = params.R_batt ?? 0.08;
  // 각 스트링 저항 초기화: 가우시안 랜덤 대신 결정론적 균등 분포 (재현성)
  const R_strings = Array.from({ length: n_strings }, (_, i) => {
    const offset = (i / Math.max(1, n_strings - 1) - 0.5) * 2 * sigma_R; // [-σ, +σ]
    return R_base * (1 + offset);
  });
  return {
    n:          n_strings,
    R_strings,                                          // 각 스트링 내부저항 [Ω]
    soc_str:    new Array(n_strings).fill(params.soc_init ?? 0.8),  // 스트링별 SOC
    cycles_str: new Array(n_strings).fill(0),           // 스트링별 누적 Ah
    t_total:    0,
  };
}

/**
 * 병렬 스트링 불균형 1스텝 계산
 *
 * 각 스트링의 전류를 내부저항 역수 비례로 배분하고 SOC를 업데이트한다:
 *   I_i = I_total × (1/R_i) / Σ(1/R_j)   [V_terminal 공유 원칙]
 *   ΔSOC_i = I_i·dt / (Q_str·3600)
 *
 * 불균형 지표 σ_soc_str와 팩 유효 용량 감소 계수 f_pack을 산출한다.
 *
 * @param {object} state     - initParallelStringState 반환 상태
 * @param {number} I_total   - 전체 팩 전류 [A] (양수=방전)
 * @param {number} soc_avg   - 팩 평균 SOC (미사용, 확장용)
 * @param {number} dt        - 타임스텝 [s]
 * @param {object} [params]
 *   params.Q_string_Ah          - 스트링 공칭 용량 [Ah] (기본 100)
 *   params.cell_imbalance_k_cap - σ_SOC → 용량 손실 계수 (기본 8.0)
 * @returns {{ state, f_pack, string_soc, string_I, sigma_soc_str, sigma_I_str, I_imbalance_pct, weakest_idx, weakest_soc }}
 */
export function computeParallelStringImbalance(state, I_total, soc_avg, dt, params = {}) {
  const { n, R_strings, soc_str } = state;
  const Q_str   = params.Q_string_Ah ?? 100;           // [Ah]
  const k_cap   = params.cell_imbalance_k_cap ?? 8.0;

  // ── 1. 전류 배분 (V_terminal 공유 원칙) ──────────────────────────────────
  // V_terminal = OCV - I*R (모든 스트링 동일)
  // I_i = I_total × (1/R_i) / Σ(1/R_j)   — 저항 역수 비례 분배
  const conductances = R_strings.map(r => 1 / r);
  const G_total      = conductances.reduce((s, g) => s + g, 0);
  const I_str        = conductances.map(g => I_total * g / G_total);  // [A] 각 스트링

  // ── 2. 스트링별 SOC 업데이트 ─────────────────────────────────────────────
  const new_soc = soc_str.map((s, i) => {
    const dSOC = (I_str[i] * dt) / (Q_str * 3600); // ΔAh → ΔSOC
    return Math.max(0, Math.min(1, s - dSOC));      // 방전=양수 전류
  });
  const new_cycles = state.cycles_str.map((c, i) =>
    c + Math.abs(I_str[i]) * dt / 3600 / (Q_str * 2)  // 풀 사이클 환산
  );

  // ── 3. 불균형 지표 산출 ──────────────────────────────────────────────────
  const soc_mean   = new_soc.reduce((s, v) => s + v, 0) / n;
  const soc_var    = new_soc.reduce((s, v) => s + (v - soc_mean) ** 2, 0) / n;
  const sigma_soc  = Math.sqrt(soc_var);             // 스트링 간 SOC 표준편차

  // 약 스트링: SOC 가장 낮은 스트링 (방전 기준 먼저 종료)
  const weakest_idx = new_soc.indexOf(Math.min(...new_soc));

  // 팩 유효 용량 감소: 약 스트링이 먼저 방전 종료 → 팩 효율 저하
  //   f_pack = 1 - k_cap × σ_soc_str (heuristic, 0.5 하한)
  const f_pack = Math.max(0.50, 1.0 - k_cap * sigma_soc);

  // ── 4. 전류 불균형 지표 (I_imb) ──────────────────────────────────────────
  const I_mean  = I_total / n;
  const I_var   = I_str.reduce((s, v) => s + (v - I_mean) ** 2, 0) / n;
  const sigma_I = Math.sqrt(I_var);
  const I_imbalance_pct = I_mean !== 0 ? +(sigma_I / Math.abs(I_mean) * 100).toFixed(2) : 0;

  return {
    state: { ...state, soc_str: new_soc, cycles_str: new_cycles, t_total: state.t_total + dt },
    f_pack:           +f_pack.toFixed(4),
    string_soc:       new_soc.map(v => +v.toFixed(5)),
    string_I:         I_str.map(v => +v.toFixed(3)),
    sigma_soc_str:    +sigma_soc.toFixed(6),
    sigma_I_str:      +sigma_I.toFixed(3),
    I_imbalance_pct,
    weakest_idx,
    weakest_soc:      +new_soc[weakest_idx].toFixed(5),
  };
}

// ── [Sprint-4] 열폭주 전파 모델 ─────────────────────────────────────────────
// ISO 6469-1 / SAE J2464 / SAND2017-6925 기반 셀 간 열폭주 전파 예측
//
// 입력:
//   triggerCell   : 트리거 셀 인덱스 (0-based)
//   cellTemps_C   : 셀별 현재 온도 배열 [°C]
//   params        : {
//     n_cells_series    : 직렬 셀 수 (예: 96)
//     T_onset_C         : 열폭주 개시 온도 [°C] (NMC 기본 180)
//     T_max_C           : 셀 최고 도달 온도 [°C] (기본 800)
//     Q_exo_Jg          : 발열량 [J/g]        (NMC 기본 1050)
//     cell_mass_g       : 셀 질량 [g]         (기본 65)
//     R_thermal_CpW     : 셀 간 열저항 [°C/W] (기본 2.5)
//     tau_propagation_s : 전파 지연 [s/cell]  (기본 45)
//   }
//
// 반환:
//   { propagation_sequence, t_total_s, max_cells_affected,
//     peak_temp_C, risk_level, mitigation }
/**
 * 1D 셀 체인 열폭주 전파 시뮬레이션
 *
 * 트리거 셀에서 시작해 양방향(좌/우)으로 전파를 시뮬레이션한다:
 *   1. 트리거 셀: Q_exo·m_cell [J] 발열 → 단열 온도 상승 dT = Q / (m·Cp)
 *   2. 인접 셀: 열 전달 P_heat = dT / R_th [W], 누적 온도 ≥ T_onset → 연쇄 발화
 *   3. 시간 진행: tau [s/cell] 전파 지연으로 전파 시퀀스 기록
 * SAE J2464 기반 위험 등급 및 대응 지침을 함께 반환한다.
 *
 * @param {number}   triggerCell   - 트리거 셀 인덱스 (0-based)
 * @param {number[]} cellTemps_C   - 셀별 초기 온도 배열 [°C]
 * @param {object}   [params]      - 열폭주 파라미터 (T_onset_C, Q_exo_Jg 등)
 * @returns {{ trigger_cell, cells_affected, propagation_ratio, t_total_s,
 *             peak_temp_C, risk_level, mitigation, propagation_sequence,
 *             cell_temps_final_C, cell_states }}
 */
export function computeThermalRunawayPropagation(triggerCell, cellTemps_C, params = {}) {
  const n          = params.n_cells_series   ?? cellTemps_C.length ?? 96;
  const T_onset    = params.T_onset_C        ?? 180;   // NMC 개시 온도 [°C]
  const T_max      = params.T_max_C          ?? 800;   // 최고 도달 온도 [°C]
  const Q_exo      = params.Q_exo_Jg        ?? 1050;  // 발열량 [J/g] (NMC: 1050, LFP: 310)
  const m_cell     = params.cell_mass_g      ?? 65;    // 셀 질량 [g]
  const R_th       = params.R_thermal_CpW    ?? 2.5;   // 셀 간 열저항 [°C/W]
  const tau        = params.tau_propagation_s ?? 45;   // 셀 간 전파 지연 [s]
  const Cp_cell    = params.cell_Cp_JgC      ?? 1.0;   // 비열 [J/(g·°C)]

  // ── 단계 1: 트리거 셀 열 발생량 계산 ──────────────────────────────────
  const Q_trigger = Q_exo * m_cell;  // [J]
  const dT_trigger = Q_trigger / (m_cell * Cp_cell);  // [°C] 단열 온도 상승

  // ── 단계 2: 인접 셀로의 열 전달 계산 ─────────────────────────────────
  // 단순화: 트리거 셀에서 양방향으로 선형 전파 (실제 팩 형상 무시)
  // P_heat = dT_trigger / R_th : 트리거 → 인접 셀 전달 파워
  const P_heat_neighbor = dT_trigger / R_th;  // [W] — 등가 열 파워

  const sequence = [];  // 전파 시퀀스 기록
  const cellState = new Array(n).fill(0);  // 0=정상, 1=위험, 2=열폭주

  // 트리거 셀 초기화
  cellState[triggerCell] = 2;
  const T_cells = [...(cellTemps_C.length === n ? cellTemps_C : new Array(n).fill(25))];
  T_cells[triggerCell] = Math.min(T_max, T_cells[triggerCell] + dT_trigger);

  sequence.push({
    t_s: 0, cell: triggerCell, event: 'TRIGGER',
    T_cell_C: +T_cells[triggerCell].toFixed(1),
    dT_C: +dT_trigger.toFixed(1),
  });

  // ── 단계 3: 전파 시뮬레이션 ───────────────────────────────────────────
  let t = 0;
  let propagating = true;
  let wave_front_left  = triggerCell - 1;
  let wave_front_right = triggerCell + 1;

  while (propagating && t < tau * n * 2) {
    propagating = false;
    t += tau;

    // 좌측 전파
    if (wave_front_left >= 0 && cellState[wave_front_left] === 0) {
      const T_neighbor = T_cells[wave_front_left];
      const dT_rcv = P_heat_neighbor * tau;  // 열 전달 [°C 등가]
      T_cells[wave_front_left] = Math.min(T_max, T_neighbor + dT_rcv);
      if (T_cells[wave_front_left] >= T_onset) {
        cellState[wave_front_left] = 2;
        // 인접 셀 발열 추가 (연쇄)
        const Q_chain = Q_exo * m_cell;
        T_cells[wave_front_left] = Math.min(T_max, T_cells[wave_front_left] + Q_chain / (m_cell * Cp_cell));
        sequence.push({
          t_s: t, cell: wave_front_left, event: 'PROPAGATE_LEFT',
          T_cell_C: +T_cells[wave_front_left].toFixed(1),
          dT_C: +(T_cells[wave_front_left] - T_neighbor).toFixed(1),
        });
        propagating = true;
      } else {
        cellState[wave_front_left] = 1;  // 위험 온도 상승 중
      }
      wave_front_left--;
    }

    // 우측 전파
    if (wave_front_right < n && cellState[wave_front_right] === 0) {
      const T_neighbor = T_cells[wave_front_right];
      const dT_rcv = P_heat_neighbor * tau;
      T_cells[wave_front_right] = Math.min(T_max, T_neighbor + dT_rcv);
      if (T_cells[wave_front_right] >= T_onset) {
        cellState[wave_front_right] = 2;
        const Q_chain = Q_exo * m_cell;
        T_cells[wave_front_right] = Math.min(T_max, T_cells[wave_front_right] + Q_chain / (m_cell * Cp_cell));
        sequence.push({
          t_s: t, cell: wave_front_right, event: 'PROPAGATE_RIGHT',
          T_cell_C: +T_cells[wave_front_right].toFixed(1),
          dT_C: +(T_cells[wave_front_right] - T_neighbor).toFixed(1),
        });
        propagating = true;
      } else {
        cellState[wave_front_right] = 1;
      }
      wave_front_right++;
    }
  }

  const cells_affected = cellState.filter(s => s === 2).length;
  const peak_temp      = Math.max(...T_cells);
  const t_total        = sequence.length > 1 ? sequence[sequence.length - 1].t_s : 0;

  // ── 위험 등급 판정 (SAE J2464 기반) ───────────────────────────────────
  const propagation_ratio = cells_affected / n;
  let risk_level, mitigation;
  if      (propagation_ratio >= 0.5) {
    risk_level  = 'CRITICAL';
    mitigation  = '즉시 차량 정지·대피 · 소방 냉각(물) 30분 이상 · 팩 교체 필수';
  } else if (propagation_ratio >= 0.2) {
    risk_level  = 'HIGH';
    mitigation  = '운행 중단 · 소방 대기 · 30분 모니터링 후 견인 처리';
  } else if (propagation_ratio >= 0.05) {
    risk_level  = 'MEDIUM';
    mitigation  = 'BMS 알림 → 안전지역 정차 · 셀 격리 후 진단';
  } else {
    risk_level  = 'LOW';
    mitigation  = '트리거 셀 격리 · BMS 모니터링 강화 · 충전 제한(C/2 이하)';
  }

  return {
    trigger_cell:       triggerCell,
    cells_affected,
    propagation_ratio:  +propagation_ratio.toFixed(3),
    t_total_s:          t_total,
    peak_temp_C:        +peak_temp.toFixed(1),
    risk_level,
    mitigation,
    propagation_sequence: sequence,
    cell_temps_final_C:  T_cells.map(v => +v.toFixed(1)),
    cell_states:         cellState,          // 0=정상, 1=위험, 2=열폭주
  };
}

// ── [Sprint-4] 열폭주 임계 진단 ─────────────────────────────────────────────
// 현재 셀 온도 배열을 스캔해 열폭주 위험 셀 식별
// 반환: { danger_cells[], warning_cells[], max_temp_C, status }
/**
 * 셀 온도 배열 스캔 — 열폭주 위험 셀 식별
 *
 * 각 셀 온도를 T_onset(개시)·T_warning(경고) 임계와 비교해
 * 위험/경고 셀 목록과 최고 온도를 반환한다.
 *
 * @param {number[]} cellTemps_C - 셀별 온도 배열 [°C]
 * @param {object}   [params]
 *   params.T_onset_C   - 열폭주 개시 온도 [°C] (기본 180)
 *   params.T_warning_C - 경고 임계 온도 [°C] (기본 120)
 * @returns {{ danger_cells, warning_cells, max_temp_C, status }}
 */
export function scanThermalRunawayRisk(cellTemps_C, params = {}) {
  const T_onset   = params.T_onset_C   ?? 180;
  const T_warning = params.T_warning_C ?? 120;  // 경고 임계 [°C]

  const danger_cells  = [];
  const warning_cells = [];
  let max_temp = -Infinity;

  cellTemps_C.forEach((T, i) => {
    if (T > max_temp) max_temp = T;
    if (T >= T_onset)   danger_cells.push({ cell: i, T_C: +T.toFixed(1) });
    else if (T >= T_warning) warning_cells.push({ cell: i, T_C: +T.toFixed(1) });
  });

  const status = danger_cells.length > 0 ? 'THERMAL_RUNAWAY'
               : warning_cells.length  > 0 ? 'WARNING'
               : 'NORMAL';

  return { danger_cells, warning_cells, max_temp_C: +max_temp.toFixed(1), status };
}

// ── [RevC-C05] 2D 팩 형상 기반 열폭주 전파 모델 ─────────────────────────
// Ioniq 5 96S2P 기준 2D 격자 모델 (n_rows × n_cols)
// 냉각판 위치·방화벽·셀 간격을 반영한 2D 확산 방정식 기반
//
// 참고:
//   - Kim et al. (2020) "Thermal runaway propagation in Li-ion battery pack"
//   - SAE J2929:2021 §5.3 — 열폭주 전파 억제 설계
//   - SAND2017-6925 — 팩 수준 열 모델
//
// 입력:
//   trigger_row, trigger_col  : 트리거 셀 격자 좌표 (0-based)
//   initTemps_2D              : 초기 온도 배열 [n_rows][n_cols] [°C]
//   params: {
//     n_rows, n_cols          : 팩 격자 크기 (기본 8×12 = 96셀)
//     T_onset_C               : 열폭주 개시 온도 (기본 180°C)
//     T_max_C                 : 셀 최고 온도 (기본 800°C)
//     Q_exo_Jg               : 발열량 (NMC 기본 1050 J/g)
//     cell_mass_g             : 셀 질량 (기본 65 g)
//     R_th_lateral_CpW        : 측면 셀간 열저항 (기본 3.0 °C/W)
//     R_th_axial_CpW          : 축방향 셀간 열저항 (기본 4.5 °C/W)
//     tau_step_s              : 시간 스텝 (기본 30 s)
//     cooling_rows[]          : 냉각판 행 인덱스 (기본 [3,7] — 4행마다)
//     firewall_cols[]         : 방화벽 열 인덱스 (없으면 빈 배열)
//     cooling_R_factor        : 냉각판 열저항 배율 (기본 0.5 — 절반으로 낮춤)
//     firewall_R_factor       : 방화벽 열저항 배율 (기본 5.0 — 5배 높임)
//   }
// 반환:
//   { final_temps_2D, cell_states_2D, propagation_log, summary }
/**
 * 2D 팩 격자 열폭주 전파 시뮬레이션 (n_rows × n_cols)
 *
 * 냉각판·방화벽 위치를 반영한 2D 확산 방정식 기반 열폭주 전파 모델:
 *   - 측면(같은 행) 열저항 R_lat, 축방향(다른 행) R_ax
 *   - 냉각판(cool_rows): 열저항 × cool_Rf (기본 0.5 → 더 잘 흡수)
 *   - 방화벽(fw_cols): 열저항 × fw_Rf (기본 5.0 → 차단 효과)
 * 매 타임스텝 모든 열폭주 셀에서 인접 4방향으로 열 전달을 계산한다.
 * SAE J2929:2021 §5.3 설계 기준 및 ISO 6469-1 기반.
 *
 * @param {number}     trigger_row   - 트리거 셀 행 (0-based)
 * @param {number}     trigger_col   - 트리거 셀 열 (0-based)
 * @param {number[][]} initTemps_2D  - 초기 온도 2D 배열 [°C] (없으면 25°C 기본)
 * @param {object}     [params]      - 격자 크기, 열저항, 방화벽/냉각판 설정 등
 * @returns {{ final_temps_2D, cell_states_2D, propagation_log, summary }}
 */
export function computeThermalRunaway2D(trigger_row, trigger_col, initTemps_2D, params = {}) {
  const rows       = params.n_rows         ?? 8;
  const cols       = params.n_cols         ?? 12;
  const T_onset    = params.T_onset_C      ?? 180;
  const T_max      = params.T_max_C        ?? 800;
  const Q_exo      = params.Q_exo_Jg      ?? 1050;
  const m_cell     = params.cell_mass_g   ?? 65;
  const Cp         = params.cell_Cp_JgC   ?? 1.0;
  const R_lat      = params.R_th_lateral_CpW  ?? 3.0;   // 측면 (같은 행)
  const R_ax       = params.R_th_axial_CpW    ?? 4.5;   // 축방향 (다른 행)
  const tau        = params.tau_step_s        ?? 30;
  const cool_rows  = new Set(params.cooling_rows   ?? [Math.floor(rows/4), Math.floor(3*rows/4)]);
  const fw_cols    = new Set(params.firewall_cols  ?? []);
  const cool_Rf    = params.cooling_R_factor  ?? 0.5;
  const fw_Rf      = params.firewall_R_factor ?? 5.0;
  const Cp_J       = m_cell * Cp;  // [J/°C] 셀 열용량

  // 초기 온도 배열 복사 (float 2D 배열)
  const T = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) =>
      (initTemps_2D && initTemps_2D[r]) ? (initTemps_2D[r][c] ?? 25) : 25
    )
  );

  // cell_state: 0=정상, 1=경고(>T_warning), 2=열폭주
  const state = Array.from({ length: rows }, () => new Array(cols).fill(0));
  const T_warning = T_onset * 0.65;  // 경고 임계 = onset의 65%

  // 발열량 [°C] — 단열 승온 (열용량 기준)
  const dT_exo = (Q_exo * m_cell) / Cp_J;

  // 트리거 셀 초기화
  state[trigger_row][trigger_col] = 2;
  T[trigger_row][trigger_col] = Math.min(T_max, T[trigger_row][trigger_col] + dT_exo);

  const log = [{
    t_s:     0,
    event:   'TRIGGER',
    row:     trigger_row,
    col:     trigger_col,
    T_C:     +T[trigger_row][trigger_col].toFixed(1),
  }];

  // 방향별 열저항 계산 함수
  function R_between(r1, c1, r2, c2) {
    const is_lateral = (r1 === r2);   // 같은 행 = 측면
    let R = is_lateral ? R_lat : R_ax;
    // 냉각판 경계 교차 시 저항 감소
    if (cool_rows.has(Math.min(r1, r2)) || cool_rows.has(Math.max(r1, r2))) R *= cool_Rf;
    // 방화벽 경계 교차 시 저항 증가
    if (fw_cols.has(Math.min(c1, c2)) || fw_cols.has(Math.max(c1, c2))) R *= fw_Rf;
    return R;
  }

  // ── 시간 전진 시뮬레이션 ─────────────────────────────────────────────
  const MAX_STEPS = rows * cols * 3;  // 최대 스텝 수 제한
  let t = 0;
  let changed = true;

  while (changed && t < MAX_STEPS * tau) {
    changed = false;
    t += tau;

    // 인접 셀에 열 전달 계산
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (state[r][c] !== 2) continue;  // 열폭주 셀만 방출

        const P_src = dT_exo / R_between(r, c, r, c);  // 등가 파워 소스 [W·°C]

        // 4방향 이웃 처리
        const neighbors = [
          [r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]
        ].filter(([nr, nc]) => nr >= 0 && nr < rows && nc >= 0 && nc < cols);

        for (const [nr, nc] of neighbors) {
          if (state[nr][nc] === 2) continue;  // 이미 열폭주
          const R = R_between(r, c, nr, nc);
          const dT_transfer = (T[r][c] - T[nr][nc]) / R * tau;  // 열 전달 [°C]
          const T_new = Math.min(T_max, T[nr][nc] + dT_transfer / Cp_J * m_cell);

          if (T_new >= T_onset && state[nr][nc] !== 2) {
            // 열폭주 개시
            state[nr][nc] = 2;
            T[nr][nc] = Math.min(T_max, T_new + dT_exo);  // 자체 발열 추가
            log.push({ t_s: t, event: 'PROPAGATE', row: nr, col: nc, T_C: +T[nr][nc].toFixed(1),
              from_row: r, from_col: c });
            changed = true;
          } else if (T_new >= T_warning && state[nr][nc] === 0) {
            state[nr][nc] = 1;  // 경고 상태
            T[nr][nc] = +T_new.toFixed(2);
          } else if (state[nr][nc] !== 2) {
            T[nr][nc] = +Math.max(T[nr][nc], T_new).toFixed(2);
          }
        }
      }
    }
  }

  // ── 요약 통계 ────────────────────────────────────────────────────────
  let n_runaway = 0, n_warning = 0, T_peak = -Infinity;
  const rows_affected = new Set();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (state[r][c] === 2) { n_runaway++; rows_affected.add(r); }
      if (state[r][c] === 1) n_warning++;
      if (T[r][c] > T_peak) T_peak = T[r][c];
    }
  }
  const total_cells  = rows * cols;
  const prop_ratio   = n_runaway / total_cells;
  const cooling_contained = !rows_affected.has(trigger_row + rows) && n_runaway <= Math.ceil(total_cells * 0.1);

  const risk_level = prop_ratio >= 0.5 ? 'CRITICAL' : prop_ratio >= 0.2 ? 'HIGH'
                   : prop_ratio >= 0.05 ? 'MEDIUM' : 'LOW';

  return {
    final_temps_2D:    T,
    cell_states_2D:    state,
    propagation_log:   log,
    summary: {
      n_runaway, n_warning,
      total_cells,
      propagation_ratio:  +prop_ratio.toFixed(4),
      rows_affected:      [...rows_affected].sort((a,b)=>a-b),
      t_total_s:          log.length > 1 ? log[log.length-1].t_s : 0,
      T_peak_C:           +T_peak.toFixed(1),
      risk_level,
      cooling_rows_active: [...cool_rows],
      firewall_cols_active:[...fw_cols],
      cooling_contained,
      mitigation: risk_level === 'CRITICAL' ? '즉시 정차·대피, 소방 30분+ 냉각'
                : risk_level === 'HIGH'     ? '운행 중단, 소방 대기, 견인'
                : risk_level === 'MEDIUM'   ? '안전지역 정차, 셀 격리'
                :                             '충전 제한, BMS 모니터링 강화',
    },
  };
}

// ── [RevC-C05] 방화벽 효과 분석 ─────────────────────────────────────────
// 방화벽 위치별 전파 억제 효과 비교 (배치 시뮬레이션)
// 반환: { configurations[], best_config }
/**
 * 방화벽 배치 효과 분석 — 다중 구성 배치 시뮬레이션
 *
 * 5가지 방화벽/냉각 구성에 대해 computeThermalRunaway2D를 반복 실행하고
 * 전파 억제 효과(containment_gain)를 비교한다:
 *   - 방화벽 없음 (기준선)
 *   - 중앙 1개 (col 6)
 *   - 3구간 2개 (col 3,9)
 *   - 4구간 3개 (col 3,6,9)
 *   - 냉각판 강화 (3행, R_factor 0.3)
 *
 * @param {number}     trigger_row   - 트리거 셀 행
 * @param {number}     trigger_col   - 트리거 셀 열
 * @param {number[][]} initTemps_2D  - 초기 온도 2D 배열
 * @param {object}     [baseParams]  - 기본 열폭주 파라미터
 * @returns {{ configurations: Array, best_config: object }}
 */
export function analyzeFirewallEffectiveness(trigger_row, trigger_col, initTemps_2D, baseParams = {}) {
  const test_configs = [
    { label: '방화벽 없음',       firewall_cols: [] },
    { label: '중앙 1개 (col 6)',  firewall_cols: [6] },
    { label: '3구간 2개 (col 3,9)',firewall_cols: [3, 9] },
    { label: '4구간 3개',         firewall_cols: [3, 6, 9] },
    { label: '냉각판 강화',       firewall_cols: [], cooling_rows: [2, 5, 8], cooling_R_factor: 0.3 },
  ];

  const results = test_configs.map(cfg => {
    const r = computeThermalRunaway2D(trigger_row, trigger_col, initTemps_2D, { ...baseParams, ...cfg });
    return {
      label:              cfg.label,
      firewall_cols:      cfg.firewall_cols ?? [],
      cooling_rows:       cfg.cooling_rows  ?? baseParams.cooling_rows,
      n_runaway:          r.summary.n_runaway,
      propagation_ratio:  r.summary.propagation_ratio,
      risk_level:         r.summary.risk_level,
      t_total_s:          r.summary.t_total_s,
      T_peak_C:           r.summary.T_peak_C,
      containment_gain:   null,  // 아래에서 계산
    };
  });

  // 기준(방화벽 없음) 대비 억제 효과
  const baseline = results[0].n_runaway;
  results.forEach(r => {
    r.containment_gain = baseline > 0
      ? +((1 - r.n_runaway / baseline) * 100).toFixed(1)
      : 0;
  });

  const best = results.slice(1).reduce((a, b) => a.n_runaway < b.n_runaway ? a : b, results[1]);
  return { configurations: results, best_config: best };
}

// ══════════════════════════════════════════════════════════════════════
// ✅ RevC 미구현→구현 ① — 3RC ECM 모델
// ══════════════════════════════════════════════════════════════════════
//
// 배경: 기존 battery2RCModelWithSEI()는 R0 + (R1‖C1) + (R2‖C2)의 2RC 구조.
//       장시간(수십 초~분) 확산 임피던스 포착 불가 → 3번째 RC 쌍 추가.
//
// 3RC 구조: V_t = OCV − I·R0 − V1 − V2 − V3
//   τ1 ≈ 1–5 s   (전기화학 이중층 응답)
//   τ2 ≈ 30–120 s (고체 내부 확산)
//   τ3 ≈ 300–600 s (장거리 Li⁺ 고체-전해질 확산)
//
// 파라미터(params):
//   R0_base, R1_base, R2_base, R3_base  [Ω]
//   C1_base, C2_base, C3_base           [F]
//   k_R0_soc_low, k_R0_soc_high        SOC 의존 저항 부스팅
//   alpha_C                             캐패시턴스 온도 계수 [/°C]
//
// 반환: { V_terminal, V_oc, P_loss, R0,R1,R2,R3, tau1,tau2,tau3, model:'3RC' }
// ══════════════════════════════════════════════════════════════════════
/**
 * 3RC 배터리 ECM 상태 초기화
 *
 * R0 + (R1‖C1) + (R2‖C2) + (R3‖C3) 구조 초기화.
 * V3는 느린 Warburg 확산 임피던스(τ3 ≈ 300–600 s)를 담당한다.
 *
 * @param {object} params
 *   params.soc_init - 초기 SOC (기본 0.8)
 *   params.T_amb    - 초기 온도 [°C] (기본 25)
 * @returns {{ SOC, V1, V2, V3, T_cell }}
 */
function init3RCBatteryState(params) {
  return {
    SOC:    params.soc_init || 0.8,
    V1:     0,
    V2:     0,
    V3:     0,
    T_cell: params.T_amb   || 25,
  };
}

/**
 * 3RC 배터리 ECM 모델 — 장시간 확산 임피던스(τ3 ≈ 300–600 s) 포함
 *
 * 2RC 대비 세 번째 RC 쌍을 추가해 수백 초 범위의 Li⁺ 고체-전해질 확산을 모사한다:
 *   V_t = OCV − I·R0 − V1 − V2 − V3
 *   dVk/dt = I/Ck − Vk/τk  (k=1,2,3)
 *   R3: Warburg 근사항 (저SOC에서 1.2×exp(-4·SOC)만큼 증가)
 *   C3: 대용량 (기본 30000 F), SOC 하강 시 8% 증가
 *
 * @param {object}      battery_state - 3RC 상태 ({ SOC, V1, V2, V3, T_cell })
 * @param {number}      I_demand      - 요구 전류 [A]
 * @param {number}      dt            - 타임스텝 [s]
 * @param {object}      params        - 차량 파라미터 (R0_base~R3_base, C1_base~C3_base 등)
 * @param {object|null} soh_state     - SOH 상태 (SEI 선택적)
 * @returns {{ V_terminal, V_oc, P_loss, R0, R1, R2, R3, tau1, tau2, tau3, SEI_loss, li_plate_risk, model }}
 */
function battery3RCModel(battery_state, I_demand, dt, params, soh_state) {
  const SOC    = battery_state.SOC;
  const V1     = battery_state.V1 || 0;
  const V2     = battery_state.V2 || 0;
  const V3     = battery_state.V3 || 0;
  const T_cell = battery_state.T_cell !== undefined ? battery_state.T_cell : 25;

  // ── OCV ─────────────────────────────────────────────────────────
  const V_oc = batteryOCV_smart(SOC, params, T_cell);

  // ── 온도 의존 저항 (Arrhenius) ────────────────────────────────
  const T_K     = T_cell + 273.15;
  const T_ref_K = (params.T_ref || 25) + 273.15;
  const Ea      = params.Ea_R0 || 3000;          // [J/mol]
  const f_temp  = Math.exp(Ea / R_GAS * (1 / T_K - 1 / T_ref_K));

  // ── SOC 의존 저항 부스팅 ─────────────────────────────────────
  const k_low  = params.k_R0_soc_low  || 0.5;
  const k_high = params.k_R0_soc_high || 0.3;
  const f_soc  = 1 + k_low  * Math.exp(-10 * SOC)
                   + k_high * Math.exp(-10 * (1 - SOC));

  // ── 캐패시턴스 온도 의존 ─────────────────────────────────────
  const alpha_C  = params.alpha_C || 0.005;
  const f_C_temp = Math.max(0.3, 1 + alpha_C * (T_cell - 25));

  // ── RC 파라미터 계산 ─────────────────────────────────────────
  const R0 = (params.R0_base || 0.020) * f_soc * f_temp;
  const R1 = (params.R1_base || 0.015) * (1 + 0.30 * Math.exp(-5 * SOC)) * f_temp;
  const R2 = (params.R2_base || 0.010) * (1 + 0.50 * Math.exp(-3 * SOC)) * f_temp;
  // R3: 느린 확산 임피던스 — Warburg 근사항으로 저SOC에서 크게 증가
  const R3 = (params.R3_base || 0.008) * (1 + 1.20 * Math.exp(-4 * SOC)) * f_temp;

  const C1 = (params.C1_base || 500)   * (1 - 0.10 * (1 - SOC)) * f_C_temp;
  const C2 = (params.C2_base || 5000)  * (1 - 0.05 * (1 - SOC)) * f_C_temp;
  // C3: 대용량 (장시간 확산), SOC 하강 시 증가 (Li⁺ 고갈 느려짐)
  const C3 = (params.C3_base || 30000) * (1 + 0.08 * (1 - SOC)) * f_C_temp;

  const tau1 = R1 * C1;  // ≈ 1–5 s
  const tau2 = R2 * C2;  // ≈ 30–120 s
  const tau3 = R3 * C3;  // ≈ 300–600 s

  // ── 1차 오일러 적분 (연속 시간 RC 방정식) ───────────────────
  //   dVk/dt = I/Ck − Vk/τk
  const dV1 = (I_demand / C1 - V1 / tau1) * dt;
  const dV2 = (I_demand / C2 - V2 / tau2) * dt;
  const dV3 = (I_demand / C3 - V3 / tau3) * dt;

  battery_state.V1 = V1 + dV1;
  battery_state.V2 = V2 + dV2;
  battery_state.V3 = V3 + dV3;

  // ── 단자전압 및 손실 ─────────────────────────────────────────
  const V_terminal = V_oc - I_demand * R0
                     - battery_state.V1 - battery_state.V2 - battery_state.V3;
  const P_loss_R0  = I_demand * I_demand * R0;
  const P_loss_R1  = (battery_state.V1 * battery_state.V1) / R1;
  const P_loss_R2  = (battery_state.V2 * battery_state.V2) / R2;
  const P_loss_R3  = (battery_state.V3 * battery_state.V3) / R3;
  const P_loss     = P_loss_R0 + P_loss_R1 + P_loss_R2 + P_loss_R3;

  // ── SEI (옵션, 2RC와 동일 로직) ─────────────────────────────
  let SEI_loss = 0, li_plate_risk = false;
  if (soh_state) {
    const k_sei_A  = params.k_sei_A  || 2.5e-7;
    const k_sei_Ea = params.k_sei_Ea || 30000;
    const k_sei    = k_sei_A * Math.exp(-k_sei_Ea / (R_GAS * T_K));
    const dSEI     = k_sei * Math.abs(I_demand) * dt;
    soh_state.SEI_thickness = (soh_state.SEI_thickness || 0) + dSEI;
    SEI_loss = soh_state.SEI_thickness * (params.R_sei_coeff || 1500);
    // Li-plating: 충전 중 음극 과전위 음수 여부
    const V_cell_pl   = V_terminal / (params.bms_cell_count_s || 96);
    const OCV_cell_pl = V_oc / (params.bms_cell_count_s || 96);
    const eta_n = V_cell_pl - OCV_cell_pl;
    li_plate_risk = (I_demand < 0 && eta_n < -(params.li_plating_eta_thresh || 0.005));
  }

  return {
    V_terminal, V_oc, P_loss, R0, R1, R2, R3,
    tau1, tau2, tau3,
    SEI_loss, li_plate_risk,
    model: '3RC',
  };
}

// ══════════════════════════════════════════════════════════════════════
// ✅ RevC 미구현→구현 ② — SPM 전해질 확산 모델 (ECM 근사)
// ══════════════════════════════════════════════════════════════════════
//
// 배경: DFN(Doyle-Fuller-Newman) 전체 PDE는 계산 비용이 과도하여 실시간 시뮬
//       레이터에 부적합. 단일입자모델(SPM)의 구 좌표계 확산 방정식을
//       Finite Difference로 이산화하여 근사.
//
// 구 좌표계 Li⁺ 확산:
//   ∂c/∂t = (D_s/r²) · ∂/∂r[ r² · ∂c/∂r ]
//   경계: ∂c/∂r|r=0 = 0 (대칭), D_s·∂c/∂r|r=R_p = −j_Li/(a·F)
//
// 이산화: Nr개 셸, 반경 격자 r_i = (i+0.5)·dr, i=0..Nr-1
//   → Crank-Nicolson 근사를 단순화한 명시적 FD 사용 (dt ≤ 0.5·dr²/D_s 조건 필요)
//
// 반환: { c_surf, c_avg, eta_conc, SOC_spm }
//   c_surf: 표면 Li 농도 [mol/m³]
//   c_avg:  평균 Li 농도
//   eta_conc: 농도 과전위 = (R·T)/(n·F)·ln(c_surf/c_avg)  [V]
//   SOC_spm: SPM SOC = c_avg / c_max
// ══════════════════════════════════════════════════════════════════════

/** SPM 확산 상태 초기화
 * @param {object} params
 *   params.spm_Nr       — 반경 이산화 수 (기본 10)
 *   params.spm_R_p      — 입자 반경 [m] (기본 5e-6)
 *   params.spm_c_max    — 최대 Li 농도 [mol/m³] (기본 31370, NMC)
 *   params.soc_init     — 초기 SOC
 */
function initSPMDiffusionState(params) {
  const Nr    = params.spm_Nr    || 10;
  const c_max = params.spm_c_max || 31370;   // [mol/m³] NMC 양극 기준
  const SOC0  = params.soc_init  || 0.8;
  // 균일 초기 농도
  const c0 = SOC0 * c_max;
  return {
    c: new Array(Nr).fill(c0),   // 각 셸 농도 [mol/m³]
    Nr,
    c_max,
    c_surf: c0,
    c_avg:  c0,
    eta_conc: 0,
    SOC_spm: SOC0,
  };
}

/** SPM 확산 스텝 (명시적 FD)
 * @param {object} spm_state   — initSPMDiffusionState() 반환값 (mutable)
 * @param {number} I_demand    — 전류 [A], 방전 양수
 * @param {number} dt          — 시간 스텝 [s]
 * @param {number} T_cell      — 셀 온도 [°C]
 * @param {object} params
 *   params.spm_R_p     — 입자 반경 [m] (기본 5e-6)
 *   params.spm_D_s_ref — 기준 확산계수 [m²/s] (기본 3.9e-14, NMC)
 *   params.spm_Ea_D    — 확산 활성화 에너지 [J/mol] (기본 35000)
 *   params.spm_a_s     — 비표면적 [m²/m³] (기본 3.8e5)
 *   params.bms_cell_count_s/p — 직·병렬 셀 수
 */
function spmDiffusionStep(spm_state, I_demand, dt, T_cell, params) {
  const Nr    = spm_state.Nr;
  const c_max = spm_state.c_max;
  const c     = spm_state.c;          // 현재 농도 배열

  const R_p   = params.spm_R_p    || 5e-6;    // [m] 입자 반경
  const D_ref = params.spm_D_s_ref || 3.9e-14; // [m²/s] 기준 확산계수
  const Ea_D  = params.spm_Ea_D   || 35000;    // [J/mol]
  const a_s   = params.spm_a_s    || 3.8e5;    // [m²/m³] 비표면적

  // Arrhenius 온도 보정
  const T_K     = (T_cell || 25) + 273.15;
  const T_ref_K = 298.15;
  const D_s     = D_ref * Math.exp(-Ea_D / R_GAS * (1 / T_K - 1 / T_ref_K));

  // 반경 격자 (셀 중심 좌표)
  const dr = R_p / Nr;
  // r_i = (i + 0.5) * dr

  // 셀당 전류 (직렬 셀 기준, 병렬 나눔)
  const n_s = params.bms_cell_count_s || 96;
  const n_p = params.bms_cell_count_p || 4;
  const I_cell = I_demand / n_p;               // [A] 셀 1개 전류

  // 표면 경계 조건: j_Li = −I_cell / (a_s · Vol_electrode · F)
  // 간략화: 단위 부피 기준 j_n = I_cell / (n_s · a_s · Vol_electrode · F)
  //   Vol_electrode ≈ 1 (단위 정규화), j_n [mol/(m²·s)]
  const F  = F_FARADAY;
  const j_n = I_cell / (a_s * (params.spm_L_elec || 7.5e-5) * F * (params.spm_electrode_area || 1.0));

  // 명시적 유한 차분 (구 좌표계)
  //   dc_i/dt = D_s/dr² · [ (r_{i+0.5}/r_i)²·(c_{i+1}−c_i) − (r_{i-0.5}/r_i)²·(c_i−c_{i-1}) ]
  //   r_i = (i+0.5)·dr
  const c_new = c.slice();
  for (let i = 0; i < Nr; i++) {
    const r_c  = (i + 0.5) * dr;              // 셸 중심 반경
    const r_rp = (i + 1.0) * dr;              // 오른쪽 면
    const r_lp = (i + 0.0) * dr;              // 왼쪽 면

    let flux_r = 0, flux_l = 0;

    // 오른쪽 플럭스
    if (i < Nr - 1) {
      flux_r = D_s * (r_rp * r_rp) / (r_c * r_c * dr * dr) * (c[i + 1] - c[i]);
    } else {
      // i = Nr-1: 표면 경계 — 갈바노스태틱 flux = −j_n
      flux_r = -D_s * (r_rp * r_rp) / (r_c * r_c * dr) * j_n;
    }

    // 왼쪽 플럭스
    if (i > 0) {
      flux_l = D_s * (r_lp * r_lp) / (r_c * r_c * dr * dr) * (c[i] - c[i - 1]);
    } else {
      // i = 0: 대칭 경계 — 플럭스 = 0
      flux_l = 0;
    }

    c_new[i] = c[i] + (flux_r - flux_l) * dt;
    // 농도 클램프 [0, c_max]
    c_new[i] = Math.max(0, Math.min(c_max, c_new[i]));
  }

  // 표면 농도: 마지막 셸 (선형 외삽)
  const c_surf = c_new[Nr - 1] + j_n * dr / D_s;  // 표면 = 셸끝 + 외삽

  // 평균 농도 (구 적분: c_avg = 3/R_p³ ∫₀^{R_p} r²·c dr)
  let sum_c = 0;
  for (let i = 0; i < Nr; i++) {
    const r_c = (i + 0.5) * dr;
    sum_c += r_c * r_c * c_new[i] * dr;
  }
  const c_avg = (3 / (R_p * R_p * R_p)) * sum_c;

  // 농도 과전위 (Nernst) η_conc
  const n_elec = 1;
  const c_surf_safe = Math.max(1, Math.min(c_max - 1, c_surf));
  const theta = c_surf_safe / c_max;
  const eta_conc = (R_GAS * T_K) / (n_elec * F) * Math.log(theta / (1 - theta + 1e-9));

  const SOC_spm = Math.max(0, Math.min(1, c_avg / c_max));

  // 상태 갱신
  spm_state.c       = c_new;
  spm_state.c_surf  = +c_surf_safe.toFixed(4);
  spm_state.c_avg   = +c_avg.toFixed(4);
  spm_state.eta_conc = +eta_conc.toFixed(6);
  spm_state.SOC_spm  = +SOC_spm.toFixed(6);

  return {
    c_surf:   spm_state.c_surf,
    c_avg:    spm_state.c_avg,
    eta_conc: spm_state.eta_conc,
    SOC_spm:  spm_state.SOC_spm,
    D_s,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// [RevE] cellBalancingOptimizer — 셀 밸런싱 최적화기
// 패시브/액티브 밸런싱 전략별 수명·효율·비용 파레토 분석
// IEC 62660-1 / USABC 배터리 관리 가이드라인 기반
/**
 * 셀 밸런싱 전략 파레토 최적화 분석
 *
 * 패시브/액티브 6종 전략의 효율·비용·수명을 비교하고 파레토 최전선을 산출한다:
 *   효율 점수: (1/t_bal × (eta_transfer + 0.01) × 10) / (cost / 100000)
 *   파레토 최전선: (효율 점수, 비용) 2차원에서 지배되지 않는 전략 집합
 *   권장 전략: 예산·수명 요건 충족 + 최고 효율 점수
 *
 * 전략 목록: 패시브 저항, 스위칭 패시브, 커패시터, 인덕터, 플라이백, 멀티포트
 *
 * @param {object} [options]
 *   options.n_cells         - 직렬 셀 수 (기본 96)
 *   options.soc_spread      - 초기 SOC 불균형 비율 (기본 0.04 = ±4%)
 *   options.Q_cell_Ah       - 셀 공칭 용량 [Ah] (기본 55)
 *   options.cycle_life_req  - 최소 요구 사이클 수명 (기본 1500)
 *   options.cost_budget_krw - 예산 [원] (기본 800,000원)
 * @returns {{ strategies, pareto_front, recommended, balancing_current_profile, ... }}
 */
function cellBalancingOptimizer({
  n_cells        = 96,
  soc_spread     = 0.04,
  Q_cell_Ah      = 55,
  cycle_life_req = 1500,
  cost_budget_krw = 800000,
} = {}) {
  const STRATEGIES = [
    { id:'passive_resistor',   name:'패시브 저항 밸런싱',      nameEn:'Passive Resistor',        I_bal_A:0.05, eta_transfer:0.00, t_bal_h:4.0, cost_krw:45000,   soh_penalty:0.020, complexity:1 },
    { id:'passive_switched',   name:'스위칭 패시브 밸런싱',    nameEn:'Switched Passive',         I_bal_A:0.10, eta_transfer:0.00, t_bal_h:2.5, cost_krw:90000,   soh_penalty:0.015, complexity:2 },
    { id:'active_capacitor',   name:'커패시터 액티브 밸런싱',  nameEn:'Capacitor Active',         I_bal_A:0.30, eta_transfer:0.92, t_bal_h:1.2, cost_krw:280000,  soh_penalty:0.008, complexity:4 },
    { id:'active_inductor',    name:'인덕터 액티브 밸런싱',    nameEn:'Inductor (Buck-Boost)',    I_bal_A:0.50, eta_transfer:0.95, t_bal_h:0.8, cost_krw:420000,  soh_penalty:0.005, complexity:5 },
    { id:'active_flyback',     name:'플라이백 DC-DC 밸런싱',   nameEn:'Flyback DC-DC Active',     I_bal_A:1.00, eta_transfer:0.93, t_bal_h:0.4, cost_krw:680000,  soh_penalty:0.004, complexity:7 },
    { id:'active_multiport',   name:'멀티포트 능동 밸런싱',    nameEn:'Multi-Port (Cell-to-Pack)',I_bal_A:2.00, eta_transfer:0.97, t_bal_h:0.2, cost_krw:1200000, soh_penalty:0.002, complexity:9 },
  ];
  const V_cell = 3.7;
  const E_imbalance_Wh = soc_spread * Q_cell_Ah * V_cell * n_cells * 0.5;

  const evaluated = STRATEGIES.map(s => {
    const E_loss_Wh  = E_imbalance_Wh * (1 - s.eta_transfer);
    const soc_impr   = 1 - soc_spread * 0.3;
    const cycle_life = Math.round(cycle_life_req * (1 - s.soh_penalty) / soc_impr);
    const capacity_util_pct = +(Math.min(100, 100 - soc_spread * 100 * (1 - s.eta_transfer / (s.eta_transfer + 0.01)))).toFixed(1);
    const efficiency_score  = +(((1/s.t_bal_h) * (s.eta_transfer + 0.01) * 10) / (s.cost_krw / 100000)).toFixed(3);
    return { ...s, E_loss_Wh:+E_loss_Wh.toFixed(2), cycle_life, capacity_util_pct, efficiency_score,
             within_budget: s.cost_krw <= cost_budget_krw, meets_life_req: cycle_life >= cycle_life_req, is_pareto:false };
  });

  const pareto_front = evaluated.filter(a =>
    !evaluated.some(b =>
      b.efficiency_score >= a.efficiency_score && b.cost_krw <= a.cost_krw &&
      (b.efficiency_score > a.efficiency_score || b.cost_krw < a.cost_krw)
    )
  ).sort((a,b) => a.cost_krw - b.cost_krw);
  pareto_front.forEach(p => { const e=evaluated.find(x=>x.id===p.id); if(e) e.is_pareto=true; });

  const recommended = pareto_front.filter(s=>s.within_budget&&s.meets_life_req)
    .sort((a,b)=>b.efficiency_score-a.efficiency_score)[0]
    ?? evaluated.filter(s=>s.within_budget).sort((a,b)=>b.efficiency_score-a.efficiency_score)[0]
    ?? evaluated[2];

  const balancing_current_profile = Array.from({length:24}, (_,h) => ({
    hour: h,
    I_bal_A: (h>=2&&h<=5) ? recommended.I_bal_A : 0,
    phase: h>=2&&h<=5 ? 'balancing' : h<2 ? 'charging' : 'idle',
  }));

  return {
    n_cells, soc_spread, Q_cell_Ah, cycle_life_req, cost_budget_krw,
    E_imbalance_Wh: +E_imbalance_Wh.toFixed(2),
    strategies: evaluated, pareto_front, recommended, balancing_current_profile,
    iso_ref: 'IEC 62660-1 / USABC Battery Management Guidelines',
    analysis_note: `셀 ${n_cells}직렬 SOC 불균형 ${(soc_spread*100).toFixed(1)}% 해소. ` +
      `권장: ${recommended.name} (비용 ${(recommended.cost_krw/10000).toFixed(0)}만원, ` +
      `균등화 ${recommended.t_bal_h}h, 수명 ${recommended.cycle_life}사이클)`,
  };
}

export {
  R_int_T,
  P_dis_max_T,
  P_chg_max_T,
  batteryOCV_LUT,

  ocvTempOffset,
  batteryOCV_Nernst,
  batteryOCV_smart,
  dOCV_dSOC,
  initEKF_SOC,
  ekfUpdateSOC,
  initUKF_SOC,
  ukfUpdateSOC,
  kalmanSOCUpdate,
  initKalmanByType,
  socUpdate,
  computeBatteryLosses,
  getSOHProfile,
  // init2RCBatteryState — re-exported from ./_shared_stubs.js (line above)
  battery2RCModelWithSEI,
  initSOHState,
  updateSOH,
  applySOHDegradation,
  initSEIState,
  updateSEI,
  liPlatingDetect,
  initBMSCells,
  bmsStep,
  // Rainflow
  initRainflowState,
  rainflowAddPoint,
  rainflowFinalize,
  rainflowDamage,
  updateSOHWithRainflow,
  // CC-CV
  initCCCVState,
  ccCVStep,
  // Cell imbalance
  initCellImbalanceState,
  updateCellImbalance,
  // OCV Hysteresis (H2 — Plett 2004)
  initHysteresisState,
  updateHysteresis,
  // DC-link spike (B-L2)
  computeDCLinkSpike,
  createOCVFunctions,  // [Fix-②-b] 멀티 케미스트리 비교용 OCV 함수 팩토리
  // ✅ RevC
  init3RCBatteryState,      // 3RC ECM 초기화
  battery3RCModel,          // 3RC ECM 스텝 (τ3 ≈ 300–600s 확산 임피던스 포함)
  initSPMDiffusionState,    // SPM 전해질 확산 초기화
  spmDiffusionStep,         // SPM 구 좌표계 FD 확산 스텝 → η_conc, SOC_spm
  // ✅ RevE
  cellBalancingOptimizer,   // 셀 밸런싱 전략 파레토 최적화 (패시브/액티브 6종)
};